import { RootUserGuideRoutes } from '../root-routes';

export interface SearchResult {
  url: string;
  subUrl: string;
  title: string;
  subTitle: string;
}

export enum IMG_SIZE {
  XS = 'xs',
  S = 's',
  M = 'm',
  L = 'l',
}

export enum BlockType {
  TEXT = 'text',
  TITLE = 'title',
  IMG = 'img',
  LIST_DOTS = 'list_dots',
  LIST_NUMBERS = 'list_numbers',
  TABLE = 'table',
  VIDEO = 'video'
}

export interface Category {
  title: string;
  url: string;
  pageContent: Array<PageContent>;
}

export interface PageContent {
  pageTitle: string;
  categories: Array<SubCategories>;
}

export interface SubCategories {
  categoriesTitle: string;
  categoriesUrl: string;
  data?: Array<{ type: BlockType; data: Array<Array<string> | string> | string; imgSize?: IMG_SIZE; startAt?: number }>;
}

export const PAGE_CONTENT: Array<Category> = [
  {
    title: 'Getting Started',
    url: RootUserGuideRoutes.GettingStarted,
    pageContent: [
      {
        pageTitle: '',
        categories: [
          {
            categoriesTitle: 'Finding Your Way Around Realist',
            categoriesUrl: 'finding-your-way-around-realist',
            data: [
              {
                type: BlockType.TEXT,
                data: 'On Realist’s main page you’ll find a full set of searching and mapping tools, all contained within one, easy-to-use interface where you’ll perform searches and view and manage your results. Searching and mapping work together seamlessly, allowing you to drill down visually on the Map with roadmap, satellite, hybrid, and terrain views, or view parcel information in the more traditional search results grid containing customizable columns of data'
              },
              {
                type: BlockType.TEXT,
                data: 'The following diagram walks you through each area of Realist’s main page and describes what you can do:'
              },
              {
                type: BlockType.IMG,
                data: 'Finding_Your_Way_Around_Realist_1.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TEXT,
                data: '<b>TIP:</b> In Realist, fields that you see in the My Search pane are called search attributes and can be edited by clicking on “Customize Search.” Search attributes can also include the areas of a Map that you search within. Since you can edit and save the attributes contained on your Searches and Forms, you can completely customize My Search for your personal use.'
              }
            ]
          },
          {
            categoriesTitle: 'Understanding My Search',
            categoriesUrl: 'understanding-my-search',
            data: [
              {
                type: BlockType.TITLE,
                data: 'Searches'
              },
              {
                type: BlockType.TEXT,
                data: 'My Search enables you to save Searches that remember the attributes you wish to search, as well as the criteria to be searched. Search using the fully customizable My Search feature to search for properties and related public record and MLS data. Use this flexible, fully configurable search to find properties by one or more search criteria.'
              },
              {
                type: BlockType.TITLE,
                data: 'Forms'
              },
              {
                type: BlockType.TEXT,
                data: 'My Search contains a set of standard forms that your MLS administrator configured for you. You can also create up to 50 of your own saved search forms. There are 2 types of saved searches: saved search form only and saved search form with values. Saved search forms will remember the fields to be searched, but will not retain any search criteria. Saved search form with values will remember the fields to be searched and values within those fields.'
              },
              {
                type: BlockType.TEXT,
                data: 'My Search may also offer several default search forms such as:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Quick Search: Quickly find a single property when you already know the Address, Owner Name, Listing Number, or APN/Folio/Tax ID (subject to change by your MLS).',
                  'General Search: Contains frequently used search criteria selected by your MLS.',
                  'Listing Search: Search by frequently used Listing information, such as Listing Number, Listing Status, List Price, and many other options.',
                  'Foreclosure: Locate properties in distress such as Pre-Foreclosure, Auction, or Bank owned/REO to look for for potential clients or to find properties for investors.',
                  'Marketing: Pinpoint properties in a specific area or near a point of interest to create custom mailing labels and expand your market presence.'
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Attributes'
              },
              {
                type: BlockType.TEXT,
                data: 'In Realist, fields that you see in the My Search pane are called search attributes and can be edited by clicking on “Customize Search.” Search attributes can also include the areas of a Map that you search within. Since you can edit and save the attributes contained on your Searches and Forms, you can completely customize My Search for your personal use.'
              },
              {
                type: BlockType.TITLE,
                data: 'Working with Search Results'
              },
              {
                type: BlockType.TEXT,
                data: 'Properties matching your search criteria are displayed in the Search Results Grid and on the Realist Map. You can continue to work with the data on the Map to refine your search, or begin working with the data on both the grid and the Map, create reports, export information to CSV files, mailing labels, and even email information directly to clients.'
              },
            ]
          },
          {
            categoriesTitle: 'Understanding the Search Results and Map',
            categoriesUrl: 'understanding-the-search-results-and-map',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The Realist Map enhances your searching by allowing you to visually see the results of your search and zoom in to actual views of the property and its surrounding location. Search results appear on the Map as push pins with the Map zoomed to a “best fit” level to see all of the matching properties. When you click a push pin, a hover box opens giving you an overview of the property and the ability to zoom in on the Map, quickly view the lot dimensions, view photos of the property (when applicable) and view reports:'
              },
              {
                type: BlockType.IMG,
                data: 'Understanding_the_Search_Results_and_Map_1.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'Mapped Properties and the Search Results Grid'
              },
              {
                type: BlockType.TEXT,
                data: 'The Map works seamlessly with the search results grid – if you click a search result row in the grid, the Map automatically highlights the corresponding property push pin on the Map. Conversely, if you select a push pin on the Map, that property is highlighted within the Search Results Grid, making property identification easier than ever.'
              },
              {
                type: BlockType.IMG,
                data: 'Understanding_the_Search_Results_and_Map_2.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'Realist Map Views'
              },
              {
                type: BlockType.TEXT,
                data: 'The Realist Map contains different views that let you see different aspects about the properties you’ve selected on the Map:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Roadmap: The Roadmap view is a standard Map view showing the roads and road names.',
                  'Satellite: The Satellite view is a satellite view of the property from above, showing property details. This view is best for identifying properties visually.',
                  'Hybrid: The Hybrid view shows a Satellite view overlaid with roads and road names.',
                  'Terrain: The Terrain view of a property shows the physical features surrounding an area where the property is located.'
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Realist Map Tools'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Drawing Tools: The drawing tools let you work directly on the Map to refine and enhance your search criteria and search results. You can use shape tools to draw a circle, rectangle, or polygon around an area in which you wish to search. For example, you can define a shape area to find properties within a certain radius of a school, a place of work, or other point of interest.',
                  'Annotations: Annotations let you make personal notes in callouts that you can place anywhere on a Map and that will be included on maps you print or email to your clients.',
                  'Boundaries: Boundaries controls the boundary lines that appear on the Map, including Zip Codes, County lines, Parcel boundaries, School District boundaries, and even Common Neighborhood boundaries.',
                  'Property and Sales Information: This tool lets you “layer” the Map with important information to consider when you are recommending a listing or purchase price on a property, including a picture of distressed properties (Pre-Foreclosure, Auction, REO/Bank owned), MLS Listings (Active, Pending, Expired), Tax and MLS Sales, ValueMap Automated Valuations, and property characteristics.',
                  'Trends: The Trends tool provides a thematic view into the sales and market trends for a particular area – either by Zip Code or common Neighborhood. These trend layers allow you to “visualize” market conditions and develop fresh interpretations of market conditions.',
                  'Points of Interest: Points of Interest helps you quickly locate points of interest in a particular area, or near a particular property. This includes things like transportation, banks, schools, restaurants, gas stations, and many others. You can use this tool in conjunction with drawing tools to measure distances or points within a radius to further refine your searches.',
                  'Driving Directions: Driving Directions lets you quickly find directions to a specific address and print or email them to yourself or a client. This tool helps you jump directly from your research into your car to get to work.',
                  'Locate on Map: This tool lets you jump to a specific address on the map or to a Zip code or city. Its purpose is to allow you to zoom to an area and use the other map tools to explore.',
                ]
              }
            ]
          },
          {
            categoriesTitle: 'Setting your User Preferences',
            categoriesUrl: 'setting-your-user-preferences',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The User Preferences page provides options for configuring personal settings for your Realist views. You can configure reports, search options, and Map options.'
              },
              {
                type: BlockType.TITLE,
                data: 'Configure Reports'
              },
              {
                type: BlockType.TEXT,
                data: 'The Configure Reports section lets you set overall report preferences for all reports and also controls settings for individual reports.'
              },
              {
                type: BlockType.TEXT,
                data: 'TIP: Reports configuration options are also available on the report and can be configured on the fly as you run reports.'
              },
              {
                type: BlockType.TITLE,
                data: 'Search Options'
              },
              {
                type: BlockType.TEXT,
                data: 'The Search Options preferences control the way information is displayed in the Search Results Grid:'
              },
              {
                type: BlockType.IMG,
                data: 'Setting_your_User_Preferences_1.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TABLE,
                data: [
                  ['Option', 'Description'],
                  ['Maximum number of properties to view in results grid', 'Select the number of properties you would like to display at one time in the Search Results Grid. The default is 250 records.'],
                  ['Display mailing label addresses registered on the Do Not Mail lists', 'Select this option to return addresses registered on the Do Not Mail list when you generate mailing labels. There will be an indicator noting that the address is on the Do Not Mail list.'],
                  ['Display phone numbers registered on the Do Not Call lists', 'Select this option to display phone numbers for people registered on the Do Not Call list. There will be an indicator noting that the person should not be called.'],
                  ['Include unmatched sale transactions in search results', 'Select this option to include unmatched sales transactions in your search results.'],
                  ['Suppress unmatched sales older than', 'Some sales, such as those for newly formed properties, cannot be matched to properties. Select this option to limit which unmatched sales transactions are returned in search results by within the last 6, 12, 18, or 24 months.'],
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Report-Specific Preferences'
              },
              {
                type: BlockType.TEXT,
                data: 'You can configure setting for the following reports:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Property Detail Report',
                  'Comparables Report',
                  'Neighbors Report',
                  'Neighborhood Profile Report'
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'These configuration options are also available within the report itself when you run it. Details on the preferences for each report are provided in Realist Reports'
              },
              {
                type: BlockType.TITLE,
                data: 'Email Signature'
              },
              {
                type: BlockType.TEXT,
                data: 'The Email Signature preference allows you to create a signature to include at the bottom of any email correspondence sent from within Realist:'
              },
              {
                type: BlockType.IMG,
                data: 'Setting_your_User_Preferences_3.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'Map Options'
              },
              {
                type: BlockType.TEXT,
                data: 'Map options control whether or not the parcel boundaries show on the Map:'
              },
              {
                type: BlockType.IMG,
                data: 'Setting_your_User_Preferences_4.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TEXT,
                data: 'Map options control whether or not the parcel boundaries show on the Map:'
              },
              {
                type: BlockType.IMG,
                data: 'Setting_your_User_Preferences_5.png',
                imgSize: IMG_SIZE.M
              }
            ]
          },
        ]
      }
    ]
  },
  {
    title: 'Features Guide',
    url: RootUserGuideRoutes.FeatureGuide,
    pageContent: [
      {
        pageTitle: 'Dashboards',
        categories: [
          {
            categoriesTitle: 'Realist Insights Dashboard',
            categoriesUrl: 'insights-dashboard',
            data: [
              {
                type: BlockType.TITLE,
                data: 'Realist Insights Dashboard User Tutorial'
              },
                {
                  type: BlockType.TEXT,
                  data: 'Welcome to the Realist Insights Dashboard, where you can harness the power of market intelligence and streamline your real estate endeavors. This comprehensive tutorial will guide you through each feature, outlining their benefits and how to best utilize them to your advantage'
                },
                {
                  type: BlockType.IMG,
                  data: 'insights-dashboard-main.png'
                },
              {
                type: BlockType.TITLE,
                data: 'Dashboard Overview'
              },
              {
                type: BlockType.TEXT,
                data: [
                  'Upon logging in, you\'ll be introduced to a dynamic dashboard that\'s tailored to meet your needs. Let\'s explore the features and their benefits:'
                ]
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 1,
                data: [
                    '<b>Quick Search Feature:</b> Efficiently find property information and market data, saving time and simplifying research.'
                  ]
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                    'Benefits: Swiftly retrieve essential property information without sifting through extensive data sets. This feature empowers you to make informed decisions efficiently, leaving you more time to focus on serving your clients.'
                  ]
              },
              {
                type: BlockType.IMG,
                data: 'dashboard-quick-search.png'
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 2,
                data: [
                  '<b>Market Temperature Indicators:</b> Visualize market conditions at a glance, helping you gauge whether the market is hot, cold, or neutral based on key metrics'
                ]
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Benefits: Stay ahead of market trends and shifts with visual indicators. The Market Temperature Indicators provide insights enable you to make strategic decisions and offer clients timely advice on buying or selling property. The data is based on the selected counties, easily customizable within Realist.'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'dashboard-market-temp.png'
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 3,
                data: [
                    '<b>Quick Stats:</b> Get real-time insights into closed sales, pending sales, and active listings, and more, aiding in data-driven decision-making'
                  ]
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Benefits: With a 90-day trend data, you can quickly adapt your strategies and provide clients with accurate insights, enhancing your credibility and success'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'dashboard-quick-stats.png'
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 4,
                data: [
                  '<b>Access to Cotality\'s Twitter Feeds:</b> Stay updated with real-time industry announcements and trends directly from the dashboard.'
                ]
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Benefits: Be at the forefront of industry developments. Stay informed about market shifts, regulatory updates, and trends, enabling you to provide clients with current insights.'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'dashboard-x-feed.png'
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 5,
                data: [
                  '<b>Access to Cotality\'s Property Intelligence Blog Posts:</b> Access expert insights and analyses on industry trends and forecasts.'
                ]
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Benefits: Elevate your knowledge and expertise. Leverage expert analyses and insights to position yourself as an authority in the market, offering clients well-informed advice.'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'dashboard-market-insights-feed.png'
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 6,
                data: [
                  'Saved Properties: Allows you to save properties that you find interesting or relevant.'
                ]
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Benefits: By saving properties, you can easily revisit and track listings that catch your attention. This comes in handy when comparing properties, monitoring changes in listings over time, and providing personalized recommendations to clients. Saved Properties streamline your workflow, helping you maintain a focused and organized approach to property management.'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'dashboard-saved-properties.png'
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 7,
                data: [
                  '<b>Access to Inman Select Articles:</b> Seamlessly access Inman Select articles for valuable industry knowledge and insights.'
                ]
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Benefits: Harness a wealth of industry expertise. Access Innam Select articles to enhance your market knowledge, empowering you to provide clients with current insights.'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'dashboard-inman-feed.png'
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 8,
                data: [
                  '<b>Personalized Saved Search Feature:</b> Create tailored saved searches to align precisely with client needs, ensuring quick access to relevant data.'
                ]
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Benefits: Customize your research to cater to your clients\' preferences. This feature enables you to access properties that match specific criteria, making property searches a breeze.'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'dashboard-saved-searches.png'
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 9,
                data: [
                  '<b>Search History:</b> Access up to 10 previous searches for quick reference and maintaining a record of property inquiries.'
                ]
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Benefits: Keep your research organized and efficient. Revisit past searches effortlessly, recall property details, and maintain a comprehensive record of interactions.'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'dashboard-last-viewed-proeprties.png'
              }
            ]
          },
          {
            categoriesTitle: 'Property Intelligence Dashboard',
            categoriesUrl: 'prop-intell-dashboard',
            data: [
              {
                type: BlockType.TEXT,
                data: 'Welcome to the Property Intelligence Dashboard! This guide will help you navigate and utilize the features of our powerful new Dashboard designed to empower real estate professionals, agents, and analysts with data-driven insights into the dynamic real estate market.'
              },
              {
                type: BlockType.TITLE,
                data: '1. Getting Started:'
              },
              {
                type: BlockType.TEXT,
                data: ['The Property Intelligence Dashboard. If the Property Intelligence Dashboard is available for your account, you can access it through the top left menu labeled “Property Intelligence”. Upon access to this page, you\'ll find a comprehensive overview of key metrics, recent updates, and access points to various features of the Dashboard.']
              },
              {
                type: BlockType.IMG,
                data: 'prop-intell-dashboard-001.png'
              },
              {
                type: BlockType.TITLE,
                data: '2. Landing Page for Property Intelligence:'
              },
              {
                type: BlockType.IMG,
                data: 'prop-intell-dashboard-002.png'
              },
              {
                type: BlockType.TEXT,
                data: [
                  'The landing page for Property Intelligence offers immediate access to valuable market insights, providing users with a quick and comprehensive snapshot of the real estate landscape. Here\'s how it benefits users:'
                ]
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Quick Stats and Trend Analysis: Users can instantly view key data points such as Closed Sales, Pending Pages, Active Listings, Days on Market, and Sale Price to List Price Ratio. Quick Stats also offers trending analysis, indicating percentage increases or decreases over the current 90-day period compared to the previous 90-day period. This feature enables users to swiftly assess market dynamics and trends. Users can start by selecting of the geographies (resolutions) such as “State” and “County” to start seeing the data visualization for the Quick Stats.',
                  'Resolution Options: Initially available resolutions include State and County, with ongoing development to include additional filtering options like CBSA and Zip Code. This allows users to focus on specific geographic areas of interest, tailoring their analysis to local market conditions.',
                  'Instantly Updated Visualization: The dashboard’s various graphs and charts are updated instantly when specific geographic filters are selected. This ensures that users have access to the specific data they are looking for. This feature allows for dynamic analysis and enables users to stay informed about evolving market trends as they adjust their filters or criteria.',
                  'Easy Data Sharing: Users can effortlessly export graphs to PDF format for download or email, facilitating seamless sharing and collaboration with clients, colleagues, or stakeholders. This sharing option enhances communication and decision-making processes.'
                ]
              },
              {
                type: BlockType.TITLE,
                data: '3. MarketTrends'
              },
              {
                type: BlockType.TEXT,
                data: 'Explore detailed market trends data, offering insights into various aspects of the real estate market.'
              },
              {
                type: BlockType.IMG,
                data: 'prop-intell-dashboard-003.png'
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 1,
                data: [
                  'Dataset: Access historical data for the current year-to-date and previous years for the following attributes'
                ]
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Total Sales - Median Price',
                  'Average Sale Price',
                  'New Construction Sales Price - Median',
                  'New Construction Sales Price - Mean',
                  'Total Sales Count',
                  'Total Transaction Volume',
                  'Total Median LTV',
                  'Loan Count',
                  'Average Sale Price vs. Days On Market',
                  '90+ Day Delinquency',
                  'REO',
                  'REO Sales Count',
                  'Foreclosures',
                  'Pre-Foreclosure Filings',
                  'Short Sales Price - Median',
                  'Short Sales Price - Mean'
                ]
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 2,
                data: [
                  'Customizable Analysis: Tailor your analysis by filtering data based on specific resolutions (State, CBSA, Zip Code, County) and time intervals (1, 2, or 3 year intervals), providing relevant insights for your market of interest.',
                  'Visual Representation: Visualize complex data through various chart types (line chart, bar chart, or pie chart), facilitating easier interpretation and analysis of market trends.',
                  'Data Sharing: Export graphs to PDF format for download or email, enabling seamless collaboration and communication with stakeholders.'
                ]
              },
              {
                type: BlockType.TITLE,
                data: '4. ListingTrends:'
              },
              {
                type: BlockType.TEXT,
                data: 'Dive into comprehensive listing trends data, providing insights into listing activities and market inventory.'
              },
              {
                type: BlockType.IMG,
                data: 'prop-intell-dashboard-004.png'
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 1,
                data: ['Dataset: Access historical data for the current year and previous years for the following attributes:']
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Active Listings DOM - Mean',
                  'Active Listings DOM - Median',
                  'Active Listings Inventory - Count',
                  'All Listings DOM - Mean',
                  'All Listings DOM - Median',
                  'All Listings Inventory - Count',
                  'Buyer/Seller Market Indicator - Mean',
                  'Buyer/Seller Market Indicator - Median',
                  'Closed Listings DOM - Mean',
                  'Closed Listings DOM - Median',
                  'Closed Listings Inventory - Count',
                  'De-Listed Listings DOM - Mean',
                  'Market Velocity',
                  'Months Supply of Active Listings',
                  'New Listings Inventory - Count',
                  'Pending Inventory - Count',
                  'Pending Listings DOM - Mean',
                  'Sold Inventory - Count',
                  'Sold Listings DOM - Mean'
                ]
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 2,
                data: [
                  'Customizable Filters: Refine your analysis by applying filters based on resolutions (State, CBSA, Zip Code, County) and time intervals (1, 2, or 3 year intervals), allowing for tailored insights specific to your market.',
                  'Data Visualization: Visualize listing trends through various chart types (line chart, bar chart, or pie chart), making it easier to understand and interpret market dynamics.',
                  'Data Export: Export listing trend graphs to PDF format for sharing or offline reference, facilitating collaboration and decision-making processes.'
                ]
              },
              {
                type: BlockType.TITLE,
                data: '5. RentalTrends'
              },
              {
                type: BlockType.TEXT,
                data: 'Explore estimated rental amounts for specific resolutions/areas, providing valuable insights into the rental market.'
              },
              {
                type: BlockType.IMG,
                data: 'prop-intell-dashboard-005.png'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Insight into Rental Market: Gain valuable insights into the rental market with estimated rental trends for different bedroom types, aiding in rental property investment decisions.',
                  'Customizable Resolutions: Analyze rental trends at different resolutions (State, County) with ongoing work to include CBSA and Zip Code filtering, ensuring relevance to your specific market.',
                  'Data Export: Easily export rental trend graphs to PDF format for download or email, enabling sharing and collaboration with stakeholders.'
                ]
              },
              {
                type: BlockType.TITLE,
                data: '6. HPI (Home Price Index):'
              },
              {
                type: BlockType.TEXT,
                data: 'Delve into detailed data points related to the Home Price Index, offering insights into property price trends.'
              },
              {
                type: BlockType.IMG,
                data: 'prop-intell-dashboard-006.png'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Property Price Insights: Delve into detailed data points related to the Home Price Index, providing insights into property price trends and fluctuations.',
                  'Customizable Filters: Customize your analysis by applying filters based on resolutions (State, County) and time intervals (1, 2, or 3 year intervals), allowing for tailored insights specific to your market. In addition, analyze HPI based on month-over-month changes vs. year-over-year changes.',
                  'Data Visualization: Visualize property price trends through various chart types (line chart, bar chart, or pie chart), facilitating easier interpretation and analysis.',
                  'Data Export: Export HPI graphs to PDF format for download or email, facilitating sharing and collaboration with stakeholders.'
                ]
              },
              {
                type: BlockType.TITLE,
                data: '7. HPI Forecasts:'
              },
              {
                type: BlockType.TEXT,
                data: 'Explore forecasts related to the Home Price Index, providing insights into future property price trends.'
              },
              {
                type: BlockType.IMG,
                data: 'prop-intell-dashboard-007.png'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Property Price Insights: Delve into detailed data points related to the Home Price Index, providing insights into property price trends and fluctuations.',
                  'Customizable Filters: Customize your analysis by applying filters based on resolutions (State, County) and time intervals (1, 2, or 3 year intervals), allowing for tailored insights specific to your market. In addition, analyze HPI based on month-over-month changes vs. year-over-year changes.',
                  'Data Visualization: Visualize property price trends through various chart types (line chart, bar chart, or pie chart), facilitating easier interpretation and analysis.',
                  'Data Export: Export HPI graphs to PDF format for download or email, facilitating sharing and collaboration with stakeholders.'
                ]
              },
              {
                type: BlockType.TITLE,
                data: '8. Exports:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Export Functionality: Export data, reports, or charts in various formats such as PDF for sharing or offline reference.'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'prop-intell-dashboard-008.png'
              },
              {
                type: BlockType.TITLE,
                data: '10. Support and Help:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Knowledge Base: Access articles, tutorials, and FAQs found on the Realist User Guide.',
                  'Contact Support: Reach out to your MLS support team for assistance or inquiries regarding the Property Intelligence Dashboard'
                ]
              }
            ]
          },
          {
            categoriesTitle: 'Assessing Market Data',
            categoriesUrl: 'insights-dashboard-assessing-market-data',
            data: [
              {
                type: BlockType.TEXT,
                data: 'Utilize Quick Stats for Real-Time Insights into Sales and Listings Trends. The Quick Stats feature is a dynamic tool that offers real-time insights into essential metrics, allowing you to stay ahead of market trends and make informed decisions. Within Quick Stats, you\'ll find the following key metrics:'
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 1,
                data: [
                  '<b>Closed Sales:</b> The number of properties that have successfully closed in a specific time frame (90 day trend). This metric gives you an understanding of recent transactional activity and provides insights into market movement.',
                  '<b>Pending Sales:</b> These are properties that have accepted offers but haven\'t yet closed. Monitoring pending sales offers a glimpse into the future market trends, helping you anticipate shifts and adjust your strategies accordingly.',
                  '<b>Active Listings:</b> The total number of properties currently available for sale. This metric reflects the current inventory level and plays a crucial role in understanding market competitiveness.',
                  '<b>Days on Market:</b> This metric reveals the average number of days it takes for properties to sell after being listed. It offers insight into the pace of the market and buyer demand. A lower value may indicate high demand and a quicker turnover.',
                  '<b>Months of Inventory:</b> Months of inventory estimates how long it would take to sell all existing listings based on the current sales pace. A lower value typically suggests a seller\'s market with limited supply, while a higher value may suggest a buyer\'s market with more available options.',
                  '<b>Sale Price to List Price Ratio:</b> This ratio indicates how close the final sale price is to the initial listing price. A ratio above 100% suggests properties are selling above their listed price, signaling a competitive market.'
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'Leveraging these metrics empowers you to interpret market trends comprehensively:'
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 1,
                data: [
                  'If there\'s a surge in Pending Sales but Days on Market is low, it might indicate a competitive seller\'s market, encouraging sellers to price strategically.',
                  'A high Months of Inventory value, coupled with a lower Sale Price to List Price Ratio, might suggest a buyer\'s market, prompting you to advise buyers on potential negotiating power.'
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'The Quick Stats feature equips you with real-time insights, helping you tailor your strategies to the current market conditions. Stay ahead of trends, provide accurate guidance to clients, and confidently navigate the dynamic real estate landscape with the Realist Insights Dashboard.'
              }
            ]
          },
          {
            categoriesTitle: 'Customization',
            categoriesUrl: 'insights-dashboard-custoization',
            data: [
              {
                type: BlockType.TEXT,
                data: 'Use the Manage Widget Capability to tailor your dashboard. Show or hide features based on your preferences.'
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 1,
                data: [
                  'Reorder widgets through simple drag-and-drop functionality for available widgets through the Manage Widget menu.'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'dashboard-reorder-widgets.png'
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 2,
                data: [
                  'Select up to 12 counties for Quick Stats, Average Sales Price, and Change In MLS Sales Activity, aligning the dashboard with your target market.'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'dashboard-select-counties.png'
              },
              {
                type: BlockType.TEXT,
                data: 'With the Realist Insights Dashboard, you have the tools to excel in the real estate landscape. Customize your experience, access valuable insights, and streamline your workflow. Prepare to embark on a journey of real estate intelligence that transforms your approach to the market.'
              }
            ]
          }
        ]
      },
      {
        pageTitle: 'Using Search',
        categories: [
          {
            categoriesTitle: 'Selecting County/Region',
            categoriesUrl: 'selecting-county-region',
            data: [
              {
                type: BlockType.TITLE,
                data: 'Smart Recall for My County'
              },
              {
                type: BlockType.TEXT,
                data: 'The last county selected in My Region is recalled each time you login.\n'
              },
              {
                type: BlockType.TITLE,
                data: 'Changing My County'
              },
              {
                type: BlockType.TEXT,
                data: 'Within the Change County panel you may select a single county from a list of all the counties your MLS serves.\n'
              },
              {
                type: BlockType.IMG,
                data: 'Selecting_County_1.png',
                imgSize: IMG_SIZE.XS
              },
              {
                type: BlockType.TEXT,
                data: 'When you execute a search, only properties within your selected county are returned:\n'
              },
              {
                type: BlockType.IMG,
                data: 'Selecting_County_2.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TEXT,
                data: 'The county you select will be the default county that is searched when you enter search criteria. This means that searches do not need to include a zip code, city or county to perform a search since the Change County panel will govern all search results.\n'
              },
              {
                type: BlockType.TEXT,
                data: '<b>NOTE:</b> If you enter a zip code or address that is in a different county available to you in the My Region panel, you’ll be able to continue your search and Realist will return results that match your entered criteria.'
              }
            ]
          },
          {
            categoriesTitle: 'Understanding Searching',
            categoriesUrl: 'understanding-searching',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The Search feature allows you to search for properties and related public record and MLS data. '
              },
              {
                type: BlockType.TITLE,
                data: 'Quick Search'
              },
              {
                type: BlockType.TEXT,
                data: 'Quick Search is a quick way to find a property based on simple attributes like the Address, Owner Name, Listing Number, or APN/Folio/Tax ID (these criteria are subject to change by your MLS administrator).'
              },
              {
                type: BlockType.TITLE,
                data: 'My Search'
              },
              {
                type: BlockType.TEXT,
                data: 'My Search  is a flexible, fully configurable search tool that offers you the ability to search using a wide-range of search criteria, including things like number of bedrooms, lot size, APN/Folio/Tax ID (subject to change by your MLS), and Owner, to name just a few. '
              },
              {
                type: BlockType.TITLE,
                data: 'How to clear the Search Template'
              },
              {
                type: BlockType.TEXT,
                data: 'You can at any time clear any outstanding searches by clicking on “Clear Searches”. This action will clear the existing search that you have the on the screen, as well as remove any “values” from the search template. Essentially, this action will momentarily convert a “search template with values” to only a “search template”, but it will not save or update the search template until you click on the save button; This means the next time you re-select the search template with values, the values will be present again.'
              },
              {
                type: BlockType.IMG,
                data: 'Understanding_Searching_1.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'Smart Search Template/Search Recall'
              },
              {
                type: BlockType.TEXT,
                data: 'The last search template or search template with values that you used will be displayed the next time you login.'
              },
              {
                type: BlockType.TITLE,
                data: 'Search Template vs Search Templates with Values'
              },
              {
                type: BlockType.TEXT,
                data: 'A Search Template is a group of blank search fields that you can create to match the information you want to search for. To search for a property using a Search Template, you enter values to the search fields (i.e. complete the form). In Realist, these search fields are called “Attributes”.'
              },
              {
                type: BlockType.TEXT,
                data: 'A Search Template with Values is essentially a search Form with values added to some of the search fields. Think of this as a completed Form that you plan to use again.'
              },
              {
                type: BlockType.TEXT,
                data: 'For example, perhaps you have a search template that includes Tax ID, Owner Name, Address, and Zip Code. If you add a zip code value to that form and save it, the search template becomes a “search template with values” that you can use over and over as needed. This is essentially a saved search.'
              },
              {
                type: BlockType.TEXT,
                data: 'This is convenient for your marketing activities in which you may save common searches used in a specific subdivision or neighborhood.'
              },
              {
                type: BlockType.TITLE,
                data: 'Search Results Grid and Realist Map'
              },
              {
                type: BlockType.TEXT,
                data: 'When you are finished adding and selecting information for the search fields, click Search at the bottom of the Search panel. Your search results appear. '
              },
              {
                type: BlockType.TEXT,
                data: 'If the search returned only a single property result, the Report window automatically opens.'
              },
              {
                type: BlockType.TEXT,
                data: 'If your search returned multiple results, the Search Results Grid shows the results and the Realist Map will zoom in to show the “best fit” view of your search results:'
              },
              {
                type: BlockType.IMG,
                data: 'Understanding_Searching_2.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TEXT,
                data: 'You can continue to work with the data on the Map or Grid to refine your search, create reports, export information to a CSV file or mailing labels, and even email information directly to clients.'
              },
              {
                type: BlockType.TEXT,
                data: '<b>NOTE:</b>. The zoom level depends on the geographic area the results cover. For example, if your search returns only one property, the Map will zoom in to that one specific area. If you search returns multiple values, the Map will display all search results with a “best fit” zoom level to ensure all properties are visible on the Map'
              },
              {
                type: BlockType.TEXT,
                data: 'In the Search Results Grid, the first three columns may contain an icon indicating the following'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'MLS Photo Indicator',
                  'MLS Listing Indicator',
                  'Foreclosure Indicator',
                  'Distressed Sale Indicator',
                ]
              }
            ]
          },
          {
            categoriesTitle: 'Using Quick Search',
            categoriesUrl: 'using-quick-search',
            data: [
              {
                type: BlockType.TITLE,
                data: 'Performing a Quick Search'
              },
              {
                type: BlockType.TEXT,
                data: 'A Quick Search is the default form that appears in the Search pane the first time you enter Realist (see Search pane in “Understanding Searches” section above. The Quick Search is a quick way to find a property based on simple attributes like the Address, Owner Name, Listing Number, or APN/Folio/Tax ID (these criteria are subject to change by your MLS administrator).'
              },
              {
                type: BlockType.IMG,
                data: 'Using_Quick_Search_1.png',
                imgSize: IMG_SIZE.XS
              },
              {
                type: BlockType.TEXT,
                data: 'To perform a Quick Search:'
              },
              {
                type: BlockType.TEXT,
                data: 'On the <b>Search pane</b>, enter information for one or more of the search fields as follows:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'To add a single value to a field, type or select the value.',
                  'Change the Search operator.',
                  'To add multiple values to a field, type or select the first value and then click the Add (+) icon. Each value will be added beneath the search field:',
                ]
              },
              {
                type: BlockType.IMG,
                data: 'Using_Quick_Search_2.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TEXT,
                data: '<b>TIP:</b> To remove a value, click the “trash bin” icon to the right the value.'
              },
              {
                type: BlockType.TEXT,
                data: 'Note that each search field contains different options, depending on the type of data that it captures. Each option is described in the following table:'
              },
              {
                type: BlockType.TABLE,
                data: [
                  ['Option', 'Description'],
                  ['drop-down list', 'Click the down arrow to select from a drop-down list of options:'],
                  ['Selection box', 'Click the Select from List button to open a box with options for selection:'],
                  ['Ranges (two fields with operator between)', 'Select the operator you would like to use to specify the range. By default, ranges are “in between”. For example, you might select “in between” 1 and 3 bedrooms. <br/> <b>TIP:</b> Entering a single value will perform a “Greater Than/Less Than or Equal To” search.'],
                  ['Dates', 'Type the date in mm/dd/yyyy format or click the calendar icon to select your date. <br/><b>TIP:</b> Quick Links for the last 3, 6, 9, or 12 months are available on the Calendar window.'],
                  ['Text Box', 'Type desired text or select from the type-ahead feature which appears after the first two letters are typed.'],
                ]
              },
              {
                type: BlockType.TEXT,
                data: '<b>TIP:</b> You can clear the form anytime and start over using the Clear Searches button at the bottom of the Search panel'
              }
            ]
          },
          {
            categoriesTitle: 'Using My Search',
            categoriesUrl: 'using-my-search',
            data: [
              {
                type: BlockType.TITLE,
                data: 'Finding Properties Using Templates and Templates with Values. '
              },
              {
                type: BlockType.TEXT,
                data: 'After you’ve performed a few searches, you’ll likely find you want to create some customized searches that fit into your own everyday work. You can create up to 25 Saved Forms and/or Searches.'
              },
              {
                type: BlockType.TITLE,
                data: 'To Create a New Form or Search from an Existing Form'
              },
              {
                type: BlockType.TEXT,
                data: 'The easiest way to create a new form or search is to build off one of the MLS-defined Forms that are available when you first log in to Realist:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['At the top of the <b>Search bar</b> select the <b>Customize Search</b> link:']
              },
              {
                type: BlockType.IMG,
                data: 'Using_My_Search_1.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['The <b>Customize Search</b> window opens. This is a list of all available search fields for your MLS (note that all fields shown in the screen shot may not be available to you):']
              },
              {
                type: BlockType.IMG,
                data: 'Using_My_Search_2.png',
                imgSize: IMG_SIZE.L
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Click the X to remove a search attribute in the Selected Attributes panel on the right. This is a list of all search attributes that are currently included on the search Form. OR Add additional search attributes to the search Form by selecting the additional attributes. Click a category in the left frame to filter the search attributes or use the Show All category to view all attributes available.',
                  'Click <b>Apply</b> when you have selected all the search attributes you would like to appear on your Form or Saved Search. The Customize Search window closes and the search Form reflects the changes you made.',
                  'Click <b>Save</b> if you would like to save the selected search attributes but want to continue staying on the Customize Search window to make additional edits',
                  'If changes are made to a custom search template but are not saved, you will be prompted to save the changes prior to navigating away from the Customize Search window.'
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'Work with attributes directly on the My Search panel to finalize the form.<br/> Click the Save icon to save the current form:'
              },
              {
                type: BlockType.IMG,
                data: 'Using_My_Search_3.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TEXT,
                data: 'The Save window opens:'
              },
              {
                type: BlockType.IMG,
                data: 'Using_My_Search_4.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'If you have added a search value to the search template, you can save the search as a <b>Search template with values</b>. When saving a search that has values, the form will automatically default to ‘Search template with values”. If you select save as a <b>Search Template</b> only, all values you’ve added to the search template will be cleared.',
                  'Add a name for the form or search. Search name should be unique',
                  'Click <b>Save</b>. Your Search Template or Search Template with value is saved. You can access it anytime from the My Search menu. '
                ]
              },
              {
                type: BlockType.TEXT,
                data: '<b>TIP:</b> Map shapes can also be saved as part of search. Refer to Using the Realist Map for details.'
              },
              {
                type: BlockType.TITLE,
                data: 'To Create a Search template or Search template with values'
              },
              {
                type: BlockType.TEXT,
                data: 'Take the following steps if you would like to create a new search template or search template with values:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['On the My Search menu, click on the dropdown and select “Create New Search”']
              },
              {
                type: BlockType.IMG,
                data: 'Using_My_Search_5.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['The <b>Customize Search</b> window opens up as a clean state.']
              },
              {
                type: BlockType.IMG,
                data: 'Using_My_Search_6.png',
                imgSize: IMG_SIZE.L
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Select the fields you would like included on your Search template. You can filter the list of available attributes by category or you can view all available fields using the Show All option.',
                  'Click <b>Apply</b>. The Search template is created and you are returned to the My Search panel, where the new selected attributes are displayed. You will then be able to enter values and then save the search template as a search template with values.',
                  'Click the <b>Save</b> icon to save the current Search template and continue working on modifying other search templates.'
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Opening an existing search template or search template with values.'
              },
              {
                type: BlockType.TEXT,
                data: 'You can open an existing search template or a search template with values by going to My Search and selecting the “search” dropdown. Within this dropdown you will find all available search templates, including admin-created and user-created ones. Note that admin-created searches appear on the list with a locked icon.'
              },
              {
                type: BlockType.IMG,
                data: 'Using_My_Search_7.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'To Open an existing search template or search template with values'
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['Find the desired search template/search template with values and simply select it. The corresponding template for the selected search will automatically populate on the My Search form']
              },
              {
                type: BlockType.TITLE,
                data: 'To Create a Search template based on an Admin-created search template'
              },
              {
                type: BlockType.TEXT,
                data: 'By default, the Admin-created searches are locked and cannot be modified. However, you are still able to select an existing admin-created search and use that as a baseline for created a new search template. To do so, you can either:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'go directly to the Customize Search window and select the admin-created search;',
                  'select the desired admin-created search template from the dropdown list.'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'Using_My_Search_8.png',
                imgSize: IMG_SIZE.L
              },
              {
                type: BlockType.TEXT,
                data: 'Now simply start selecting additional attributes and the locked search template will automatically switch to a brand new one:'
              },
              {
                type: BlockType.IMG,
                data: 'Using_My_Search_9.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TEXT,
                data: 'You can now select ‘Apply’ to start using the search based on the newly selected criteria. '
              },
              {
                type: BlockType.TEXT,
                data: 'You can also optionally provide a search name although it is not required at this time. Selecting “save” will require a search name to be entered.'
              },
              {
                type: BlockType.TEXT,
                data: '<b>Important!</b> This search menu list displays all the available search templates and search templates with values, with the newest ones appearing at the top of the list. If you notice that the list does not match up with your list of searches, clear the cache in your browser to update the list.'
              }
            ]
          },
          {
            categoriesTitle: 'Searching on the Realist Map',
            categoriesUrl: 'searching-on-the-realist-map',
            data: [
              {
                type: BlockType.TEXT,
                data: 'Your searches may start out on the My Search Panel, but the Realist Map includes tools that help you gather additional information to build or expand your search criteria. When properties that match your search criteria are found, they are displayed on the Map with a push pin. You can click the push pin to zoom in on the property or run reports, including property detail, comparable properties in the area, market trends like sales price and market conditions, a complete neighborhood profile, and foreclosure data.'
              },
              {
                type: BlockType.TEXT,
                data: '<b>NOTE:</b> It is important to note that the Map always takes precedence over other searches. Once you begin working on the Map, the criteria of the search will relate directly to what is happening on the Map, and some fields, like Zip Code and City, will be disabled in the Search Results Grid.'
              },
              {
                type: BlockType.TITLE,
                data: 'Saved Shape Searches'
              },
              {
                type: BlockType.TEXT,
                data: 'You can also use drawing tools to mark the search boundary(ies) that will be added to your My Search Form. When a shape is drawn on the Map, it automatically becomes part of your My Search Template with values.'
              },
              {
                type: BlockType.TEXT,
                data: '<b>NOTE:</b> Drawing a shape on the map is an excellent way to search for parcels in a particular subdivision. Because of the subtle differences in how a particular subdivision name appears on a parcel record, physically identifying a subdivision by its boundaries may be a better way to ensure you receive your expected results. These shapes can then be saved as a Saved Search to reuse later:'
              },
              {
                type: BlockType.IMG,
                data: 'Searching_on_the_Realist_Map_1.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'For More Information'
              },
              {
                type: BlockType.TEXT,
                data: 'For detailed information on using the Shape Drawing Tools, refer to <u>Working with Drawing Tools.</u>'
              },
              {
                type: BlockType.TITLE,
                data: 'Boundaries'
              },
              {
                type: BlockType.TEXT,
                data: 'Using Boundaries you can view important information related to properties that helps to visualize surrounding features such as parcel boundaries (or Neighborhoods, County, City, Zip Code, School District, etc.), and Points of Interest, which highlight nearby banks, restaurants, schools, etc.'
              },
              {
                type: BlockType.TITLE,
                data: 'Quick Identify'
              },
              {
                type: BlockType.TEXT,
                data: 'Quick identify lets you quickly view a parcel boundary by clicking any area on the Map.'
              },
              {
                type: BlockType.TEXT,
                data: 'This brings up the Property Detail Card:'
              },
              {
                type: BlockType.IMG,
                data: 'Searching_on_the_Realist_Map_2.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'For More Information'
              },
              {
                type: BlockType.TEXT,
                data: 'For detailed information on using the mapping features, refer to <u>Using the Realist Map.</u>'
              },
            ]
          },
          {
            categoriesTitle: 'Ways to Work with Search Results',
            categoriesUrl: 'ways-to-work-with-search-results',
            data: [
              {
                type: BlockType.TEXT,
                data: 'After you execute a search, the search results appear in the Search Results Grid and the Map automatically zooms in to the location of the property(ies). How you work with this data is entirely up to you and it may vary depending on the type of search you performed.'
              },
              {
                type: BlockType.TEXT,
                data: 'The Search feature is designed to be flexible enough to support the variety of tasks you may need to complete during your workday.'
              },
              {
                type: BlockType.TITLE,
                data: 'Workday Scenarios'
              },
              {
                type: BlockType.TEXT,
                data: 'The following scenarios show some ways you can use your search results:\n'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'You recently sold a house in a particular neighborhood and want to follow-up with a marketing mailing to the neighbors. Using a search you can find the neighboring properties and quickly generate mailing labels from your Search Results Grid.',
                  'You are interested in finding out more about a known property and its location. You can run a single property search to view information directly on the Search Results Grid using the standard columns, and then add any additional data columns you are interested in. You can even see on the Map where the property is located, what’s nearby, and what the area is like. And, if you find the property might interest a client, you can easily email the property information to them right from the Search Results Grid.',
                  'You would like to analyze data internally at your office. You can run your search and then print or export the data directly from the Search Results Grid.',
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Views'
              },
              {
                type: BlockType.TEXT,
                data: 'There are several ways you can choose to view data:\n'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  '<b>Map:</b> This view will collapse the Search Results Grid and display a larger version of the Map.',
                  '<b>List:</b> This view will collapse the Map and display only the Search Results Grid.',
                  '<b>Split:</b> This is the default view. The Map and Search Results Grid show simultaneously.\n'
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'When you access Realist, the Map and the search grid results both display in Split view:'
              },
              {
                type: BlockType.IMG,
                data: 'Ways_to_Work_with_Search_Results_1.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'View All 3,000 Results'
              },
              {
                type: BlockType.TEXT,
                data: 'You now have the option to select up to 3,000 search results back when searching in Quick Search or My Search. This feature is especially helpful when exporting or generating mailing labels for a large number of records from your searches.'
              },
              {
                type: BlockType.TEXT,
                data: 'At the bottom of the Search Pane you will be able to switch between 1-1000, 1001-2000, and 2001-3000 results.'
              },
              {
                type: BlockType.IMG,
                data: 'Ways_to_Work_with_Search_Results_2.png',
                imgSize: IMG_SIZE.S
              }
            ]
          },
          {
            categoriesTitle: 'Search Results Grid',
            categoriesUrl: 'search-results-grid',
            data: [
              {
                type: BlockType.TEXT,
                data: 'When you access Realist, the Map and the search Table results both display in Split view:'
              },
              {
                type: BlockType.IMG,
                data: 'Search_Results_Grid_1.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TEXT,
                data: 'Split view is the default view, but you can also change it using the Change View options at the top right corner:'
              },
              {
                type: BlockType.IMG,
                data: 'Search_Results_Grid_2.png',
                imgSize: IMG_SIZE.XS
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  '<b>Split:</b> Click this option to view both the Map and Search Results Table.',
                  '<b>Map:</b> Click this view to close the Search Results Table and show only the Map.',
                  '<b>List:</b> Click this view to close the Map and only show the Search Results Table.'
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Default Sort Order'
              },
              {
                type: BlockType.TEXT,
                data: 'Search results are sorted by APN/Folio/Tax ID by default.'
              },
              {
                type: BlockType.TITLE,
                data: 'Selections'
              },
              {
                type: BlockType.TEXT,
                data: 'When search results are returned initially, all results on the list are selected and if you are viewing the Realist Map, all the properties will display on the Map:'
              },
              {
                type: BlockType.TEXT,
                data: 'The selected items will also be used if you choose to export, email, or print the search results.'
              },
              {
                type: BlockType.TEXT,
                data: 'To change the selections, click a check box to deselect an item or click the check box at the top of the column to clear all selections. This is helpful when you want to select a single property to locate on the Map or run a report on.'
              },
              {
                type: BlockType.TITLE,
                data: 'Columns'
              },
              {
                type: BlockType.TEXT,
                data: 'The Search Results Table displays columns of data for each property record your search returned (these will be set by your MLS Administrator). You can show more or less information using the Customize <b>Table</b> menu.'
              },
              {
                type: BlockType.IMG,
                data: 'Search_Results_Grid_3.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TEXT,
                data: '<b>NOTE:</b> You can select up to 30 columns to display, however, the more columns you select to appear, the more scrolling will be required to view the information.'
              },
              {
                type: BlockType.TEXT,
                data: 'There are also four indicator columns for each property. The following table describes each icon:\n'
              },
              {
                type: BlockType.TABLE,
                data: [
                  ['Option', 'Description'],
                  ['MLS Photo Indicator', 'Indicates that the property has MLS photo images associated with it. You can click the photo indicator icon to open up a photo viewer and view the images.'],
                  ['MLS Listing Indicator', 'Indicates that the property has an MLS listing associated with it. You can click the indicator icon to open up the MLS listing.'],
                  ['Foreclosure ', '<b>Pre-foreclosure:</b> Preforeclosed properties still belong to the owner. The lender has just begun the foreclosure process due to a default on loan payments. <br/> <b>Auction:</b> If the owner is unable to make the necessary payments or sell the property, it goes to a public auction. <br/> <b>Bank Owned (REO):</b>  A bank owned, or Real Estate Owned (REO) property has not been sold at auction and is now in the possession of the lender.'],
                  ['Distressed ', '<b>Short Sale:</b> A short sale is a property that is in foreclosure but is up for sale by the lender before it goes to auction. The lender must agree to accept less than the amount that is owed on the property. <br/> <b>Bank Sales (REO):</b> A distressed bank owned property is one that has been repossessed by the lender but has not gone through the foreclosure process. '],
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Show or Hide Columns on the Search Results Table'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'To remove or add columns, click on Customize Table link at the top of the table. This will allow you to select additional attributes to add the search results table',
                  'To add additional attributes to the search table, simply check box for any attributes you would like to add. Select a category in the left frame to filter the list or use the Show All view to see all attributes you can add.',
                  'Click Apply to make the changes. You are returned to the Search Results Table, which shows the new column selections.',
                  'Click Cancel to cancel out of any changes.'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'Search_Results_Grid_8.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'Sorting the Search Results'
              },
              {
                type: BlockType.TEXT,
                data: 'You can sort your search results by clicking on any of the column headers. Clicking once on any column will sort the results in descending order. Clicking twice will sort the results in ascending order. Once a particular column has been sorted, the Table header will appear with the arrow highlighted in blue:'
              },
              {
                type: BlockType.IMG,
                data: 'Search_Results_Grid_9.png',
                imgSize: IMG_SIZE.S
              }
            ]
          },
          {
            categoriesTitle: 'Search Results Cards',
            categoriesUrl: 'search-results-cards',
            data: [
              {
                type: BlockType.TITLE,
                data: 'Card view'
              },
              {
                type: BlockType.TEXT,
                data: 'Card view allows you to view the search results in “card” layout. To view the search results in “card” layout, simply run a property search and then click on the “card” option on the grid to switch the view.'
              },
              {
                type: BlockType.IMG,
                data: 'Search_Results_Cards_1.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TEXT,
                data: 'Once you have selected this option, the search results automatically switch over to card view, as seen below:\n'
              },
              {
                type: BlockType.IMG,
                data: 'Search_Results_Cards_2.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'Default Attributes'
              },
              {
                type: BlockType.TEXT,
                data: 'It’s important to note that when viewing search results in card view option, there are both fixed and customizable attributes that allow you to customize the most relevant attributes to display on the results cards.'
              },
              {
                type: BlockType.TITLE,
                data: 'Fixed Attributes'
              },
              {
                type: BlockType.TEXT,
                data: 'The fixed attributes (these cannot be changed) are the following:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'MLS Photo (If available)',
                  'MLS Status Indicator (If available)',
                  'MLS Sales Price (If available)',
                  'Site Address',
                  'Beds',
                  'Baths',
                  'Building Sq Ft'
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Customizable Attributes'
              },
              {
                type: BlockType.TEXT,
                data: 'There are up to 3 customizable attributes that you can customize when in card view. To do so, simply click on the <b>Customize Cards</b> link. This will open up the window to allow you to select up to 3 attributes to customize and present on the card. Once attributes are selected, click on <b>Apply</b> button to save the selection.'
              },
              {
                type: BlockType.IMG,
                data: 'Search_Results_Cards_3.png',
                imgSize: IMG_SIZE.M
              },
            ]
          },
          {
            categoriesTitle: 'Saved Properties',
            categoriesUrl: 'saved-properties',
            data: [
              {
                type: BlockType.TEXT,
                data: 'Saved Properties is a new feature that we’re making available as part of the next-generation Realist. With Saved Properties you will be able to select, while in card view, to add properties to a watched list, so that you can return to it the next time you login or access the Realist system. With the Saved Properties feature, you can save up to 50 properties and then you’ll also be able to click to view property detail reports, export, or generate mailing labels.'
              },
              {
                type: BlockType.TITLE,
                data: 'Adding Properties to Saved Properties List'
              },
              {
                type: BlockType.TEXT,
                data: 'You must be in card view in order to add properties to your saved properties list. Once you are in card view, simply click on the “star” icon to add a property to the saved properties list. Once selected, the star icon will be highlighted for that specific property, along with the saved properties count being updated in real-time under the “My Saved Properties” icon in the top menu. '
              },
              {
                type: BlockType.TEXT,
                data: 'You can always take a quick look on the Saved Properties by clicking on the star icon to expand the menu – this will allow you to view all the properties that you have added to the list.'
              },
              {
                type: BlockType.IMG,
                data: 'Saved_Properties_1.png',
                imgSize: IMG_SIZE.L
              },
              {
                type: BlockType.TEXT,
                data: 'Additionally, you can choose to navigate to the Saved Properties page to view all Saved Properties that you have added by clicking on <b>‘Go to Saved Properties”</b> button.'
              },
              {
                type: BlockType.IMG,
                data: 'Saved_Properties_2.png',
                imgSize: IMG_SIZE.L
              },
              {
                type: BlockType.TITLE,
                data: 'Saved Properties Page'
              },
              {
                type: BlockType.TEXT,
                data: 'The Saved Properties page is where you can view all the properties that have been previously added. All saved properties – up to 50 properties – will continue to appear on this page until you select to remove them from the saved list. Within this page, you’ll also be able to export, download, email, create labels, and view reports for all the saved properties. '
              },
              {
                type: BlockType.TEXT,
                data: 'To remove a property from the saved properties list, simply make sure the box is checked for that specific property and then click on the <b>Remove from Saved</b> button at the top right corner. Properties that are removed will immediately disappear from the saved properties list.'
              },
              {
                type: BlockType.TEXT,
                data: 'At any given, you can navigate back to the Realist homepage by clicking on the Back button at the top left.'
              },
              {
                type: BlockType.IMG,
                data: 'Saved_Properties_3.png',
                imgSize: IMG_SIZE.M
              }
            ]
          },
          {
            categoriesTitle: 'Exporting Search Results',
            categoriesUrl: 'exporting-search-results',
            data: [
              {
                type: BlockType.TEXT,
                data: 'If you need to export your property data, you can perform an export of the Table, or a custom export containing additional information, to a CSV file that can be viewed in Microsoft Excel.'
              },
              {
                type: BlockType.TEXT,
                data: '1.\tSelect the records you would like to export. If you have a long list of search results, click the check box at the top of the column to clear all selections, and then select only the properties you want to export.'
              },
              {
                type: BlockType.TEXT,
                data: '2.\tClick the Export option at the bottom of the Search Results Table and select one of the following options:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  '<b>Quick Export:</b> Select this option to export the information that currently appears on the Search Results Table. You will be able check or uncheck to remove attributes, but not add new ones.'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'Exporting_Search_Results_1.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  '<b>Customize Export:</b> Select this option to export additional information. You will be able to choose from the full list of search field attributes. Selected fields will be saved automatically for the next time you want to export records.'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'Exporting_Search_Results_2.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TEXT,
                data: '3.\tSelect or remove attributes from the export.'
              },
              {
                type: BlockType.TEXT,
                data: '4.\tClick Export.'
              },
              {
                type: BlockType.TEXT,
                data: '<b>TIP:</b> Depending on the number of property records being exported, it may be necessary to wait several minutes. Do not close the browser or navigate away from the page until the export is complete.'
              }
            ]
          },
          {
            categoriesTitle: 'Downloading Search Results.',
            categoriesUrl: 'downloading-search-results',
            data: [
              {
                type: BlockType.TEXT,
                data: 'Select the property record(s) you would like to download. If you have a long list of search results, click the check box at the top of the column to clear all selections, and then select only the properties you want to include in PDF format:'
              },
              {
                type: BlockType.IMG,
                data: 'Downloading_Search_Results_1.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TEXT,
                data: 'Click Download at the bottom of the Search Results Table and select one of the following options:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  '<b>Report:</b> Select this option to include the full property detail report in the email. You can email a maximum of 25 results',
                  '<b>Map and</b> Table: Select this option to include an image of the Map as it currently displays on the Realist search page as well as the information for the selected property(ies) as displayed in the Search Results Table. You can email a maximum of 100 results.',
                  '<b>Map Only:</b> Select this option to only include the Map image as it appears on the search page. You can email a maximum of 100 results.',
                  '<b>Table Only:</b> Select this option to only send the selected properties and information on the Search Results Table. You can email a maximum of 100 results',
                  '<b>Cards Only:</b> Select this option to only send the selected properties and information on the Search Results Card View. You can email a maximum of 50 card results',
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Note:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Due to the various possible lengths of each data field (as well as the possible absence of data) the summary report above has spaced parcel data in a standardized format. This eliminates the option for a true one-liner style report but gives you the option to print multiple columns (of your choosing) from your Table in an organized manner',
                  'Due to the various possible lengths of each data field (as well as the possible absence of data) the summary report above has spaced parcel data in a standardized format. This eliminates the option for a true one-liner style report but gives you the option to print multiple columns (of your choosing) from your Table in an organized manner',
                  'Click Print. A PDF doc is created in a separate window, for printing .'
                ]
              }
            ]
          },
          {
            categoriesTitle: 'Emailing Search Results',
            categoriesUrl: 'emailing-search-results',
            data: [
              {
                type: BlockType.TEXT,
                data: 'After you’ve viewed the properties within the Search Results Table and Map, there may be some properties you want to email to a client. Take the following steps to email the information in PDF format directly from the Search Results Table:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['Select the properties on the Search Results Table you would like to email. If you have a long list of search results, click the check box at the top of the column to clear all selections, then select only the properties you want to email.']
              },
              {
                type: BlockType.TEXT,
                data: 'Click Email at the bottom of the Search Results Table and select one of the following options:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  '<b>Report:</b> Select this option to include the full property detail report in the email. You can email a maximum of 25 results',
                  '<b>Map and Table:</b> Select this option to include an image of the Map as it currently displays on the Realist search page as well as the information for the selected property(ies) as displayed in the Search Results Table. You can email a maximum of 100 results',
                  '<b>Map Only:</b> Select this option to only include the Map image as it appears on the search page. You can email a maximum of 100 results.',
                  '<b>Table Only:</b> Select this option to only send the selected properties and information on the Search Results Table. You can email a maximum of 100 results',
                  '<b>Cards Only:</b> Select this option to only send the selected properties and information on the Search Results Card View. You can email a maximum of 50 card results'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'Emailing_Search_Results_1.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['Type the contents of your email, just like you were sending an email from your regular email program. Click <b>Send</b>. An email with a PDF attachment is sent to the person indicated in the “To” field.']
              },
              {
                type: BlockType.TEXT,
                data: '<b>TIP: </b>The email comes from Cotality with the your name and the report name in the Subject line. The email address in the from line of the dialog window may be auto-filled from the last address entered. If your email address has changed simply re-move the displayed address and enter your new email.'
              }
            ]
          },
          {
            categoriesTitle: 'Creating Mailing Labels',
            categoriesUrl: 'creating-mailing-labels',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The Mailing Labels feature is great way to promote the marketing side of your business. The property Search Results Table is integrated with an Avery mailing label template, so you can go from the Table directly to printing labels for a mass mailing. One valuable use for this is to target neighbors when you’ve made a sale in a neighborhood. Run a search to find all houses in that neighborhood (or draw it on the Map), and then generate labels and send a mailing promoting your proven success in selling in their area.'
              },
              {
                type: BlockType.TITLE,
                data: 'To Create Mailing Labels'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Run a search to find the properties for which you want to print mailing labels.',
                  'On the Search Results Table, make sure all the properties you want to include are selected',
                  'Click on Create Labels option at the bottom of the Search Results Table',
                  'The Labels window opens:'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'Creating_Mailing_Labels_1.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Select the type of Avery label you will be using. Note that Avery 5161 and 5162 support the use of USPS Bar Codes. If you’d like to include the bar code, select the check box beneath the type of label you are using.',
                  'Select whether you’d like to use Mixed Case or All Capital Letters in the labels.',
                  'Select whether to use the Tax Billing address or the Property address. For example, if you are mailing to locate property owners in a rental area of condominiums, you’d likely want to use the Tax Billing address so the mailing goes to the property owner rather than the renter.TIP: Select Create Custom Label to add “or Current Resident” and other greetings to the mailing address.',
                  'Review your Export Status to ensure you will have enough remaining exports to cover the labels. If not, you can use up your remaining exports. Click the Use all remaining to export a portion of the records check box.',
                  'Select Print Range if you would like to create labels for a subset of your search results you may enter the starting row number and the ending row number (range) to include these in the labels.',
                  'Click Print. A RTF file containing the mailing labels is created. Save this file to a known location on your computer or print from within the RTF viewer to send them to your printer',
                  'Add the Avery labels to your printer, and print the RTF file.'
                ]
              }
            ]
          },
          {
            categoriesTitle: 'Premium Map Layers for Cotality Property Insights Platform',
            categoriesUrl: 'premium-map-layer',
            data: [
              {
                type: BlockType.TITLE,
                data: 'Easily spot opportunities using vivid color coding'
              },
              {
                type: BlockType.TEXT,
                data: 'Premium Map Layers is an advanced feature that makes it easy to filter and identify opportunities on the map using color-coded property parcels.'
              },
              {
                type: BlockType.TEXT,
                data: 'With Premium Map Layers, you can begin your search by choosing a map layer that isolates a specific attribute.  Available attributes include:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  '<b>Building Type</b>',
                  '<b>County Land Use</b>',
                  '<b>Home Equity</b>',
                  '<b>Last Sale Price</b>',
                  '<b>Mortgage Age</b>',
                  '<b>Mortgage Rate</b>',
                  '<b>Owner Occupied</b>',
                  '<b>Ownership Time</b>',
                  '<b>Property Value</b>',
                  '<b>Rent Score</b>',
                  '<b>Sell Score</b>'
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'When you choose a map layer, the program instantly color codes the parcels to indicate different value ranges, making it easy to spot properties or neighborhoods with the desired attribute. Value ranges can be toggled off to only highlight the parcels you’re interested in.'
              },
              {
                type: BlockType.TEXT,
                data: 'Once a map layer is selected, you can click individual parcels to see their exact values.  You can further narrow your search using 100+ different tax and listing criteria, such as recording date, sale date, settlement date, subdivision, status, etc.  Results can be exported, used to create mailing labels, and more.'
              },
              {
                type: BlockType.IMG,
                data: 'Premium-1.svg',
                imgSize: IMG_SIZE.L
              },
              {
                type: BlockType.TITLE,
                data: 'Step 1:  Select your County and Navigate to My Search'
              },
              {
                type: BlockType.TEXT,
                data: 'To begin using Premium Map Layers, you must select the desired County where the map-based searches will be conducted. It’s important to note that Premium Map layers only supports a single-county search at this time. Once you have selected the desired County, click on My Search to access the Premium Map Layers menu.'
              },
              {
                type: BlockType.IMG,
                data: 'Premium-2.svg',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'Step 2:  Select the desired layer from the Premium Map Layers Menu'
              },
              {
                type: BlockType.TEXT,
                data: 'The list of 11 Premium Map layer menus are featured on the drop-list. Simply select the desired layer from the drop down menu have the layer appear on the map. It’s important to note that once a layer is selected, you must also toggle on each of the associated values to begin seeing the colors populating at the parcel level.'
              },
              {
                type: BlockType.LIST_NUMBERS,
                data: [
                  'Select the desired layer from the Premium Maps Layer menu',
                  'By default, all values are set to “OFF”',
                  'To turn each value on, select “Toggle All” button or click on the desired value to toggle from “OFF” to “ON”',
                  'Please note: colors start populating at 1,000 ft, so be sure to zoom in if you are not seeing the color-coded parcels'
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Default values are set to OFF'
              },
              {
                type: BlockType.IMG,
                data: 'Premium-3.svg',
                imgSize: IMG_SIZE.L
              },
              {
                type: BlockType.TITLE,
                data: 'Values Set to ON'
              },
              {
                type: BlockType.IMG,
                data: 'Premium-4.svg',
                imgSize: IMG_SIZE.L
              },
              {
                type: BlockType.TITLE,
                data: 'Step 3:  Search'
              },
              {
                type: BlockType.TEXT,
                data: 'Once any of the desired values are toggled from OFF to ON, the search button will be enabled, to allow you to start the search based on the selected Premium Map layer and the values that have been toggled ON. When searching with just the Premium Map attribute, the results returned simply reflect the selected values. However, to enhance your search further, you can choose to include other tax-derived attributes (as would be the case with a standard Saved Search that exists under the My Search drop-down selection). Up to 30 tax-derived attributes can be used in addition to the Premium Map search, in order to narrow the search results. Once you have refined the search results, you can proceed to send the results to export, mailing labels, or print.'
              },
              {
                type: BlockType.TITLE,
                data: 'Refine Premium Map Searches With Tax Attributes'
              },
              {
                type: BlockType.IMG,
                data: 'Premium-5.svg',
                imgSize: IMG_SIZE.L
              },
              {
                type: BlockType.TITLE,
                data: 'Results Can Be Exported or Mail Merged'
              },
              {
                type: BlockType.IMG,
                data: 'Premium-6.svg',
                imgSize: IMG_SIZE.L
              },
            ]
          },
          {
            categoriesTitle: 'Realmailers Postcards',
            categoriesUrl: 'realmailers-postcards',
            data: [
              {
                type: BlockType.TITLE,
                data: 'Introducing Realist’s New RealMailers Integration: Seamless Direct Mail Marketing for You'
              },
              {
                type: BlockType.IMG,
                data: 'Realmailers_Postcards_1.png',
                imgSize: IMG_SIZE.S,
              },
              {
                type: BlockType.TEXT,
                data: 'We’re thrilled to bring you Realist’s new integration with RealMailers, designed specifically to simplify your marketing efforts and give you an edge in finding your next listing. With Realist’s powerful prospecting tools like the Sell Score and Premium Map Layers, you can now build precise mailing lists targeting homeowners who are likely ready to sell.'
              },
              {
                type: BlockType.TEXT,
                data: 'With just a few clicks, you can export these targeted leads directly to RealMailers for a smooth transition into direct mail marketing. No more juggling data or manual processes—focus on what you do best: growing your business and building strong client relationships.'
              },
              {
                type: BlockType.TITLE,
                data: 'Here’s how it benefits you:',
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Effortless Export: Generate highly targeted mailing lists with Realist’s comprehensive data, then export up to 1,000 records directly to RealMailers with ease.',
                  'Customizable Options: Tailor your exports to match your marketing strategy by choosing the detail level and address type, whether it’s the Tax Billing Address or Property Address.',
                  'Simple Integration: Enjoy direct access to RealMailers right within Realist, no need for complex third-party tools to launch your postcard campaigns.',
                  'Free Trial & Low-Cost Subscription: Take advantage of a 6-month free trial from Realmailers, followed by just $4.99 per month to continue using the service.',
                ],
              },
              {
                type: BlockType.TEXT,
                data: 'With this integration, you have everything you need to take your prospecting and direct mail marketing to the next level, all from within the Realist platform.'
              },
              {
                type: BlockType.TITLE,
                data: 'Follow these simple steps to get started:',
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 1,
                data: [
                  '<b>Start Your Search</b>',
                ],
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Begin by performing an <b>Address Search, Map-Based Search using polygons</b>, or use <b>My Search</b> to apply complex filters such as tax attributes, property types, or owner characteristics.',
                ],
              },
              {
                type: BlockType.IMG,
                data: 'Realmailers_Postcards_2.png',
                imgSize: IMG_SIZE.S,
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 2,
                data: [
                  '<b>Refine Your Results</b>',
                ],
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Use tools like the <b>Realist Sell Score</b> and <b>Premium Map Layers</b> to zero in on properties with the highest likelihood of being sold. This helps you create a more focused and effective mailing list.',
                ],
              },
              {
                type: BlockType.IMG,
                data: 'Realmailers_Postcards_3.png',
                imgSize: IMG_SIZE.S,
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 3,
                data: [
                  '<b>Navigate to the "Send Postcards" Button</b>',
                ],
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Once you’ve identified your target properties, navigate to the <b>"Send Postcards"</b> button within Realist.',
                ],
              },
              {
                type: BlockType.IMG,
                data: 'Realmailers_Postcards_4.png',
                imgSize: IMG_SIZE.S,
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 4,
                data: [
                  '<b>Make Your Selections</b>',
                ],
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Choose between exporting the <b>Tax Billing Address</b> or the <b>Property Address</b>, depending on your marketing strategy. You can export up to 1,000 records at a time.',
                ]
              },
              {
                type: BlockType.IMG,
                data: 'Realmailers_Postcards_5.png',
                imgSize: IMG_SIZE.XS,
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 5,
                data: [
                  '<b>Click "Create" to Proceed to RealMailers</b>',
                ],
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'After making your selections, click <b>"Create"</b> to be taken directly to the RealMailers website, where you can finalize your postcard design and launch your campaign.',
                ]
              },
              {
                type: BlockType.IMG,
                data: 'Realmailers_Postcards_6.png',
                imgSize: IMG_SIZE.XS,
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 6,
                data: [
                  '<b>Finalize and Send</b>',
                ],
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'On the RealMailers site, you’ll have the opportunity to review, customize, and place your order for postcards.',
                ],
              },
            ]
          }
        ]
      },
      {
        pageTitle: 'Using Realist Map',
        categories: [
          {
            categoriesTitle: 'About the Realist Map',
            categoriesUrl: 'about-the-realist-map',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The Realist Map enhances your searches by allowing you to visually see the results of your search and zoom in to actual views of the property and its surrounding location. Search results appear on the Map as push pins. When you click a push pin, a hover box opens giving you an overview of the property and the ability to zoom in on the Map or run reports:'
              },
              {
                type: BlockType.IMG,
                data: 'About_the_Realist_Map_1.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'Quick Identify '
              },
              {
                type: BlockType.TEXT,
                data: 'You can also use the Quick Identify feature to find information on other properties with a click of the Map. When you click any area of the Map, Quick Identify will show you the parcel boundary (if boundaries are not on by default) and will also bring up the Property Detail hover box so you can get more information on the property:'
              },
              {
                type: BlockType.IMG,
                data: 'About_the_Realist_Map_2.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'Property Detail and Quick Identify Hover Boxes '
              },
              {
                type: BlockType.TEXT,
                data: 'The Property Detail and Quick Identify hover boxes that appear when you select a property on the Map provide an overview of the property, including the'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Address',
                  'Owner',
                  'APN/Folio/Tax ID',
                  'Number of bedrooms and bathrooms',
                  'Last sale date',
                  'Quick Zoom link: Click this to zoom to a close-up, best view zoom level of the property location.',
                  'View Report link: Click this link to bring up the Report window for the property to view the Property Detail, Comparables, Market Trends, Neighbors, and Neighborhood Profile reports and when available an Assessor Map, property images, and a Foreclosure report.'
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Mapped Properties and the Search Results Grid'
              },
              {
                type: BlockType.TEXT,
                data: 'The Map works seamlessly with the Search Results Grid – if you click a search result row in the grid, the Map automatically zooms to the area the property is located on the Map. Conversely, if you select a push pin on the Map, that property is selected on the Search Results Grid.'
              },
              {
                type: BlockType.IMG,
                data: 'About_the_Realist_Map_3.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'Ways to use the Map'
              },
              {
                type: BlockType.TEXT,
                data: 'The Realist Map can be used to enhance searches you’ve already completed using My Search, or you may choose to start your search right from the Map. Some ways you might use the Map include the following:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'You’ve found a property using My Search and you’d like to take a closer look. The Map automatically zoomed to the property you found. You can check out the property using a birdseye view to virtually “walk around” the property and see what the outside and yard looks like.',
                  'You want to search properties within a specific group of streets. You can draw a shape on the Map and perform a search within the boundaries of the shape.',
                  'You just sold a property and want to do a targeted mailing to homeowners nearby. Zoom in to the property on the Map and use the rectangle tool to draw around the surrounding neighborhoods. Then, conduct a “shape search” to find all homes in the selected area and generate mailing labels directly from the search results.',
                  'You have a particular area you are interested in, but only want properties within one mile of a school. You can find points of interest on the Map in the area, select the school, and use the circle tool to find all properties within a one mile radius of the school.'
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'These are just a few examples of the many ways you can use the Map. As you read through this section and review the available tools, you’ll likely think of many other ways you can use the Map in your own workflow.'
              }
            ]
          },
          {
            categoriesTitle: 'Understanding Realist Map Tools',
            categoriesUrl: 'understanding-realist-map-tools',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The Map tools allow you to customize the Map to give you a better perspective on a search area. '
              },
              {
                type: BlockType.TEXT,
                data: 'This gives you the ability to visually scan important information about a property and its surrounding areas and also easily target properties in specific geographic areas.'
              },
              {
                type: BlockType.TITLE,
                data: 'Accessing the Map Tools'
              },
              {
                type: BlockType.TEXT,
                data: 'The Realist Map tools are available on the toolbar at the top of the Map.'
              },
              {
                type: BlockType.IMG,
                data: 'Understanding_Realist_Map_Tools_1.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TEXT,
                data: 'Click on a tool to open the tool options. Each tool is described in the sections that follow.'
              },
              {
                type: BlockType.TEXT,
                data: '<b>TIP:</b> You can display up to six combinations of Map boundaries and trend layers simultaneously. The number of items selected appears at the bottom of the Boundaries and Trends tool windows'
              },
              {
                type: BlockType.TITLE,
                data: 'Drawing Tools'
              },
              {
                type: BlockType.TEXT,
                data: 'The drawing tools let you work directly on the Map to refine and enhance the area you are looking at:'
              },
              {
                type: BlockType.IMG,
                data: 'Understanding_Realist_Map_Tools_2.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TEXT,
                data: 'The shape tools allow you to draw a circle, rectangle, or polygon around an area to focus a search within the shape area, find properties within a certain radius of a school or other point of interest, and measure distances. You can draw up to five shapes covering an area up to 6.875 miles.'
              },
              {
                type: BlockType.TITLE,
                data: 'Boundaries'
              },
              {
                type: BlockType.TEXT,
                data: 'The Boundaries tool controls boundary lines that appear on the Map, including zip codes, county lines, parcel boundaries, school district boundaries, and even neighborhood boundaries:'
              },
              {
                type: BlockType.IMG,
                data: 'Understanding_Realist_Map_Tools_3.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'Property and Sales Information'
              },
              {
                type: BlockType.TEXT,
                data: 'Property and Sales Information lets you display property information such as Foreclosures, MLS Listings, Recent Sales, and Property Characteristics on the Map to provide a holistic view of the activity in a given area. This information is especially helpful when conducting searches using the Map or to show activity around a subject property.'
              },
              {
                type: BlockType.IMG,
                data: 'Understanding_Realist_Map_Tools_4.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'Trends'
              },
              {
                type: BlockType.TEXT,
                data: 'The Trends tool gives you a quick view into the sales and market trends for a particular area – either by zip code or neighborhood'
              },
              {
                type: BlockType.IMG,
                data: 'Understanding_Realist_Map_Tools_5.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'Points of Interest'
              },
              {
                type: BlockType.TEXT,
                data: 'The Points of Interest tool helps you quickly locate points of interest in a particular area, or near a particular property, including things like banks, schools, restaurants, gas stations, and many more. You can use this tool in conjunction with drawing tools to measure distances or points within a radius.'
              },
              {
                type: BlockType.IMG,
                data: 'Understanding_Realist_Map_Tools_6.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'Driving Directions'
              },
              {
                type: BlockType.TEXT,
                data: 'The Driving Directions tool lets you quickly find directions to a specific address and print or email them to yourself or a client. This tool helps you go directly from your research to your car and off to the property.'
              },
              {
                type: BlockType.IMG,
                data: 'Understanding_Realist_Map_Tools_7.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'Locating Address on the Map.'
              },
              {
                type: BlockType.TEXT,
                data: 'Locate on Map allows you to quickly find a property or city by typing in the address, city, or landmark name. As you type the address, city, or landmark name, the predictive text provides a list of probable matches. Simply select from this list of matches to view the property, city, or landmark. The result will appear at the center of your Realist map screen. '
              },
              {
                type: BlockType.IMG,
                data: 'Understanding_Realist_Map_Tools_8.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'Measurement Tool '
              },
              {
                type: BlockType.TEXT,
                data: 'The Measurement tool allows you to quickly measure the distance between two or more points on a map.'
              },
              {
                type: BlockType.IMG,
                data: 'Understanding_Realist_Map_Tools_9.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'Annotation Tool'
              },
              {
                type: BlockType.TEXT,
                data: 'You can annotate the Map so a cloud appears near an area with any notes you want to appear on the Map when you print it.'
              },
              {
                type: BlockType.IMG,
                data: 'Understanding_Realist_Map_Tools_10.png',
                imgSize: IMG_SIZE.S
              }
            ]
          },
          {
            categoriesTitle: 'Working with Drawing Tools',
            categoriesUrl: 'working-with-drawing-tools',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The drawing tools let you work directly on the Map to refine and enhance the area you are looking at. There is a set of drawing tools:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Radius Tool',
                  'Rectangle Tool',
                  'Polygon Tool',
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'Each is described in the sections that follow.'
              },
              {
                type: BlockType.TITLE,
                data: 'Enhancing Searches with the Shape Tools'
              },
              {
                type: BlockType.TEXT,
                data: 'The shape tools allow you to draw a circle, rectangle, or polygon around an area on the Map and then search within that shape. Once you draw the shape, it is added to the My Search panel and becomes part of you search. When a shape or shapes have been added to the My Search panel, all search results will be restricted to data within the shape(s). In some cases this may differ from your selected My Region County. Additionally, when shapes are drawn on the Map the search attributes for City and Zip Code are disabled so that they do not interfere with your shape-driven search.'
              },
              {
                type: BlockType.IMG,
                data: 'Working_with_Drawing Tools_1.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'Common Uses for the Shape Tools'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'One common use for the shape tool would is to draw a circle with a mile radius around a property and then search within the radius for similar properties.',
                  'You can also use the drawing tools in conjunction with the Points of Interest tool. Set the Points of Interest view to “Education” to display schools on the Map and then draw a circle around a desirable school to quickly find all listed properties within a five mile radius of the school.',
                  'The free-form line tool is a great way to measure distances. Say you are curious about how close a property is to a school or a set of train tracks. Draw a line between the two using the tool and you’ll see the exact distance when the line is selected.',
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'To Draw a Shape'
              },
              {
                type: BlockType.LIST_NUMBERS,
                data: [
                  'Find the location on the Map you would like to work with or zoom in to the preferred region.',
                  'On the Drawing Tools toolbar, click the type of shape you would like to draw. ',
                  'Click the spot on the Map where you’d like to draw the shape once (don’t hold the mouse button down).',
                  'Move your mouse up, down, to the right, or to the left to draw the shape.',
                  'Click once to end the shape for a circle or square. If you are drawing a polygon or free-form line or shape, single click for each point and then double click when the shape is complete.',
                ]
              },
              {
                type: BlockType.TEXT,
                data: '<b>NOTE:</b> You can draw up to five shapes on the Map at one time. All the shapes will become the focus of the search in the search grid. Shapes cannot be larger than 44 acres or five miles.'
              },
              {
                type: BlockType.TITLE,
                data: 'Understanding the Shape Dialog Box'
              },
              {
                type: BlockType.TEXT,
                data: 'You can work with a shape on the Map by single-clicking it. This opens the shape dialog box:'
              },
              {
                type: BlockType.IMG,
                data: 'Working_with_Drawing Tools_2.png',
                imgSize: IMG_SIZE.XS
              },
              {
                type: BlockType.TEXT,
                data: 'The shape dialog box shows the position points of the shape (the gray dots), the number of square feet in the shape itself, and the total number of square feet in all shapes on the Map at that time. For example, in the image above the circle has a Shape Area of 2.983 square miles. There is also a square and polygon on the Map, so the Total Area of all three shapes is 9.271 square miles.'
              },
              {
                type: BlockType.TEXT,
                data: '<b>TIP:</b> You can click in the white text box to rename the search. This is recommended if you will be saving the shape as part of a search in the My Search pane (see Saving a Shape Search below).'
              },
              {
                type: BlockType.TITLE,
                data: 'Moving the Shape'
              },
              {
                type: BlockType.TEXT,
                data: 'Once you’ve drawn the shape you can select it on the Map to drag it to a different location. Make sure the red frame appears around the shape before you drag it (otherwise you will end up resizing it).'
              },
              {
                type: BlockType.TITLE,
                data: 'Resizing the Shape'
              },
              {
                type: BlockType.TEXT,
                data: 'To resize the shape, single click on the shape and then click any one of its connection points and drag it to resize.'
              },
              {
                type: BlockType.TITLE,
                data: 'Saving a Shape Search'
              },
              {
                type: BlockType.TEXT,
                data: 'You can also name the shape and save it as a saved search or form for future use. The shape will appear at the top of the My Search pane with the default name or your shape name, if you have added one. The following is a circle shape for a search for properties within five miles of an elementary school:'
              },
              {
                type: BlockType.IMG,
                data: 'Working_with_Drawing Tools_3.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TEXT,
                data: 'Save your search or form as you normally would. The shape will be saved as part of the search and will appear when you search for properties using the saved search or form in the future.'
              },
              {
                type: BlockType.TITLE,
                data: 'Deleting Shapes'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Click the Delete Shape button on the hover box that appears when the shape is selected OR',
                  'Remove Shape from the My Search Panel.'
                ]
              }
            ]
          },
          {
            categoriesTitle: 'Location Boundaries on the Map',
            categoriesUrl: 'location-boundaries-on-the-map',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The Boundaries tool controls what boundaries you see on the Map. This is a useful tool for checking to ensure a property is exactly where you want it to be if you need it to fall within school boundaries or within a certain zip code, for example.'
              },
              {
                type: BlockType.TITLE,
                data: 'Available Boundary Views'
              },
              {
                type: BlockType.TEXT,
                data: 'There are four standard boundary views that you will always have – Parcel, Zip Code, City, and County. School District (Unified, elementary, or Secondary), and Common Neighborhood (when available) are configured by your MLS system administrator.'
              },
              {
                type: BlockType.TITLE,
                data: 'Parcel'
              },
              {
                type: BlockType.TEXT,
                data: 'When the parcel boundary is turned on, the property lines for all parcels appear:'
              },
              {
                type: BlockType.IMG,
                data: 'Location_Boundaries_on_the_map_1.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TEXT,
                data: '<b>TIP:</b> To view boundaries, you must be zoomed in to the Map. If you are viewing a very large area the boundary lines will not appear. There is a zoom in or zoom out icon which will appear on the boundary menu if you are not at the correct zoom level'
              },
              {
                type: BlockType.TEXT,
                data: 'You can set Parcel view as the default view in your user preferences. '
              },
              {
                type: BlockType.TITLE,
                data: 'Zip Code'
              },
              {
                type: BlockType.TEXT,
                data: 'This option displays the zip code boundaries for the area. You may need to zoom out to see these boundaries:'
              },
              {
                type: BlockType.IMG,
                data: 'Location_Boundaries_on_the_map_2.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'City'
              },
              {
                type: BlockType.TEXT,
                data: 'The City boundaries option shows the city lines:'
              },
              {
                type: BlockType.IMG,
                data: 'Location_Boundaries_on_the_map_3.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'County'
              },
              {
                type: BlockType.TEXT,
                data: 'The County boundaries option shows the county lines:'
              },
              {
                type: BlockType.IMG,
                data: 'Location_Boundaries_on_the_map_4.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'Unified School District'
              },
              {
                type: BlockType.TEXT,
                data: 'This option shows all elementary and high school boundaries:'
              },
              {
                type: BlockType.IMG,
                data: 'Location_Boundaries_on_the_map_5.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'Neighborhood'
              },
              {
                type: BlockType.TEXT,
                data: 'This view shows the neighborhood boundaries, if available. Urban areas are more likely to have these boundaries:'
              },
              {
                type: BlockType.IMG,
                data: 'Location_Boundaries_on_the_map_6.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'To Turn on a Boundary'
              },
              {
                type: BlockType.LIST_NUMBERS,
                data: [
                  'On the Map Toolbar, click the Boundaries icon.',
                  'Select one or more boundaries you would like to display. You may need to zoom in or out on the Map to see the desired boundary. As you select each boundary, it will appear on the Map.'
                ]
              },
              {
                type: BlockType.TEXT,
                data: '<b>NOTE:</b> You can select up to six boundaries.'
              },
              {
                type: BlockType.TITLE,
                data: 'To Turn Off a Boundary'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'To turn off one or more boundaries, open the Boundaries tool and click the boundary check box to deselect it OR',
                  'Click the “X”  button next to the name of each map layer enabled'
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Using Quick Identify'
              },
              {
                type: BlockType.TEXT,
                data: 'You can always quickly view a parcel boundary by clicking any area of the Map. This also brings up the property detail hover box where you can view owner information and access the Quick Zoom and Reports links for the parcel or property.'
              }
            ]
          },
          {
            categoriesTitle: 'Property and Sales Information on the Map',
            categoriesUrl: 'property-and-sales-information-on-the-map',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The Property and Sales Information tool lets you display property information such as Foreclosures, MLS Listings, Recent Sales, and Property Characteristics on the Map to provide a holistic view of the activity in a given area. This information is especially helpful when conducting searches using the Map or to show activity around a subject property.'
              },
              {
                type: BlockType.TEXT,
                data: 'There are five categories of property and sales information. You can view six layers of data simultaneously.'
              },
              {
                type: BlockType.TITLE,
                data: 'Distressed Properties'
              },
              {
                type: BlockType.TEXT,
                data: 'This category offers the stages of foreclosure as well as short sales. When you select this option, distressed properties will be indicated on the Map with the appropriate icon:'
              },
              {
                type: BlockType.IMG,
                data: 'Property_and_Sales_Information_on_the_Map_1.png',
                imgSize: IMG_SIZE.XS
              },
              {
                type: BlockType.TEXT,
                data: '<b>NOTE:</b> These properties may have a Foreclosure report available in the View Report window'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Pre-foreclosure: Pre-foreclosed properties still belong to the owner. The lender has just begun the foreclosure process due to a default on loan payments.',
                  'Auction: If the owner is unable to make the necessary payments or sell the property, it goes to a public auction.',
                  'Bank Owned (REO): A Bank owned, or Real Estate Owned (REO) property has not been sold at auction and is now in the possession of the lender.',
                  'Short Sale: Cotality’s short sale flag is applied to properties if the most recent sale price is less than the previous mortgage.',
                  'Bank Owned: A distressed bank owned property is one that has been repossessed by the lender but has not gone through the foreclosure process.',
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Sales and Values'
              },
              {
                type: BlockType.TEXT,
                data: 'This category offers Cotality sales in the last 3, 6, 9, or 12 months and AVM layers:'
              },
              {
                type: BlockType.IMG,
                data: 'Property_and_Sales_Information_on_the_Map_2.png',
                imgSize: IMG_SIZE.XS
              },
              {
                type: BlockType.TEXT,
                data: '<b>NOTE:</b> Our AVM’s provide reliable and realistic valuations of property values based on existing data, such as the current listing and recent sales, and rolls it into a valuation that you can use when listing or making an offer on a property.'
              },
              {
                type: BlockType.TITLE,
                data: 'MLS Status'
              },
              {
                type: BlockType.TEXT,
                data: 'This category offers listings by MLS status and MLS sales within the last 3, 6, 9, or 12 months:'
              },
              {
                type: BlockType.IMG,
                data: 'Property_and_Sales_Information_on_the_Map_3.png',
                imgSize: IMG_SIZE.XS
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Active: the seller is soliciting offers through the MLS and no offer has yet been accepted',
                  'Pending: The seller has accepted an offer and is no longer soliciting offers through the MLS',
                  'Expired: The seller is not soliciting offers through the MLS because the time frame of the listing contract has run out.'
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'NOTE: In some cases this data may be more current than the county tax record data.'
              },
              {
                type: BlockType.TITLE,
                data: 'Property Characteristics'
              },
              {
                type: BlockType.TEXT,
                data: 'This category contains labels that you can show one at a time to indicate various things about the property, including:'
              },
              {
                type: BlockType.IMG,
                data: 'Property_and_Sales_Information_on_the_Map_4.png',
                imgSize: IMG_SIZE.XS
              }
            ]
          },
          {
            categoriesTitle: 'Researching Market Trends',
            categoriesUrl: 'researching-market-trends',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The Market Trends tool lets you view market trends that will give you key data for your pricing and listing activities directly on the Map. You can view trends across large areas and you can also get a detail view of actual values for a selected zip code or neighborhood. This tool makes it easier for consumers to understand what is happening in a market area.'
              },
              {
                type: BlockType.TITLE,
                data: 'To Turn on Market Trends'
              },
              {
                type: BlockType.IMG,
                data: 'Researching_Market_Trends_1.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.LIST_NUMBERS,
                data: [
                  'On the Map Toolbar, select the Trends icon, shown above.',
                  'The Trend Layer window opens. Select one of the following Trend options:'
                ]
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  '<b>Aggregation Level:</b> Select whether to show market trends by zip code or by neighborhood.',
                  '<b>Property Type:</b> Select the type of properties you want to view trends for – either single family residence (SFR), condo, or both. ',
                  '<b>Opacity Scale:</b> Select the opacity of the colors that will appear on the Map showing the trends. 100% is solid and 10% is almost completely see through, allowing you to see all the road names and any other information currently displayed on the Map. ',
                  '<b>Trend Type:</b> Select the type of trend you would like to show. You can only view one trend at a time.After you select the trend, it will appear on the M'
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Getting Actual Values for a Zip Code or Neighborhood'
              },
              {
                type: BlockType.TEXT,
                data: 'To get the specific values for the trend for a zip code or neighborhood, hold the CTRL key down on your keyboard and single click on the area. A trend detail box opens:'
              },
              {
                type: BlockType.IMG,
                data: 'Researching_Market_Trends_2.png',
                imgSize: IMG_SIZE.XS
              }
            ]
          },
          {
            categoriesTitle: 'Finding Points of Interest',
            categoriesUrl: 'finding-points-of-interest',
            data: [
              {
                type: BlockType.TEXT,
                data: 'You can use the Points of Interest tool to populate an area you are viewing with points of interest, such as schools, banks, transportation, and restaurants. You can select items from the following list:'
              },
              {
                type: BlockType.IMG,
                data: 'Finding_Points_of_Interest_1.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TEXT,
                data: 'As you select items, icons for the points of interest appear on the Map. The following screen shot shows educational institutions showing on the Map:'
              },
              {
                type: BlockType.IMG,
                data: 'Finding_Points_of_Interest_2.png',
                imgSize: IMG_SIZE.XS
              },
              {
                type: BlockType.TITLE,
                data: 'Taking a Closer Look'
              },
              {
                type: BlockType.TEXT,
                data: 'Click a point of interest to view specifics – the name of a Bank, for example, shown above. Points of Interest show up at 500ft zoom level. If you do not see specific points of interest upon selection, be sure to zoom in to view them on the map.'
              },
              {
                type: BlockType.TEXT,
                data: '<b>TIP:</b> You may want to use the Map drawing tools to enhance the points of interest you’ve found on the Map. You could find condos within a 1/2 mile radius of a train station or all single family residences within a two mile radius of a particular school. To do this, draw a shape around the point of interest and perform a “shape search” on the search grid.'
              }
            ]
          },
          {
            categoriesTitle: 'Finding Driving Directions',
            categoriesUrl: 'finding-driving-directions',
            data: [
              {
                type: BlockType.TEXT,
                data: 'Once you have completed your research, you might have some properties you’d like to head out to view. You can use the Driving Directions tool to get directions and print or email them to yourself or to a client.'
              },
              {
                type: BlockType.TITLE,
                data: 'To Get Driving Directions'
              },
              {
                type: BlockType.IMG,
                data: 'Finding_Driving_Directions_1.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.LIST_NUMBERS,
                data: [
                  'Click the Driving Directions tool on the Map toolbar',
                  'The Driving Directions window opens.',
                  'Type a Drive From and Drive To location, including number, street, city, and zip code.',
                  'Select <b>“Get Directions”</b>.',
                  'Map is updated with a path from start to finish and written directions appear in the Driving Directions window:',
                ]
              },
              {
                type: BlockType.IMG,
                data: 'Finding_Driving_Directions_2.png',
                imgSize: IMG_SIZE.XS
              },
              {
                type: BlockType.TITLE,
                data: 'To Clear Driving Directions'
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['To remove the directions from the Map and clear the Driving Directions window, click Clear on the Driving Directions window']
              }
            ]
          },
          {
            categoriesTitle: 'Locating Address on the Map',
            categoriesUrl: 'locating-address-on-the-map',
            data: [
              {
                type: BlockType.TEXT,
                data: 'Locate on Map allows you to quickly find a property or city by typing in the address, city, or landmark name. As you type the address, city, or landmark name, the predictive text provides a list of probable matches. Simply select from this list of matches to view the property, city, or landmark. The result will appear at the center of your Realist map screen. '
              },
              {
                type: BlockType.IMG,
                data: 'Locating_Address_on_the_Map_1.png',
                imgSize: IMG_SIZE.S
              }
            ]
          },
          {
            categoriesTitle: 'Using Annotation Tool',
            categoriesUrl: 'using-annotation-tool',
            data: [
              {
                type: BlockType.TITLE,
                data: 'Adding Notes to the Map with the Annotation Tool'
              },
              {
                type: BlockType.TEXT,
                data: 'The Annotation tool lets you add notes to the Map that will appear when you print the Map out for yourself or for a client:'
              },
              {
                type: BlockType.IMG,
                data: 'Using_Annotation_Tool_1.png',
                imgSize: IMG_SIZE.XS
              },
              {
                type: BlockType.TITLE,
                data: 'To Add an Annotation'
              },
              {
                type: BlockType.LIST_NUMBERS,
                data: [
                  'Find the area of the Map you’d like to add a note to and zoom in to the preferred region.',
                  'On the Drawing Tools toolbar, click the <b>Annotation Tool</b>.',
                  'Click the location on the Map where you would like the note to appear.',
                  'The Add Annotation box opens.',
                  'Type the text of your annotation in the box.',
                  'Click <b>Apply</b>. The formatted annotation appear on the Map'
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'To Delete an Annotation'
              },
              {
                type: BlockType.TEXT,
                data: 'To delete an annotation, select the annotation box on the Map and click <b>Delete</b> on the hover box.'
              }
            ]
          },
          {
            categoriesTitle: 'Using Measurement Tool',
            categoriesUrl: 'using-measurement-tool',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The measurement tool, also known as line tool, allows you to measure the distance between two points, or multiple points. To start, simply select the tool from the map menu (ruler icon) and then click anywhere on the map to start drawing. You have the flexibility to click to draw a line between any two points, or draw multiple points creating any custom shape desired. To stop drawing the measurement tool, simply <b>double left click </b> on the map on the last point drawn. This action this will disengage the drawing tool. Next, <b>left click </b> once on the line drawn to view the total length. The length will be displayed in total yards.'
              },
              {
                type: BlockType.IMG,
                data: 'Using_Measurement_Tool_1.png',
                imgSize: IMG_SIZE.XS
              }
            ]
          }
        ]
      },
      {
        pageTitle: 'Realist Reports',
        categories: [
          {
            categoriesTitle: 'Running a Report',
            categoriesUrl: 'running-a-report',
            data: [
              {
                type: BlockType.TEXT,
                data: 'There are two ways to run a report:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'From the Search Results Grid',
                  'From the Realist Map'
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'To Run a Report from the Search Results Grid'
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['On the Search Results Grid, select the properties for which you would like to run reports and Click “Reports”:']
              },
              {
                type: BlockType.IMG,
                data: 'Running_a_Report_1.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'The Property Detail Report for the selected property opens',
                  'Click a different report tab to view a report for the current property, or “X” to close and to move on to view reports for another property.'
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'To Run a Report from the Realist Map'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'On the Map, click the push pin for the property. A property information hover box opens',
                  'Click View Reports. The Detail Report for the property opens.',
                  'Click a different report tab to view another report for the current property.',
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Working with Reports'
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['Complete details on each report are provided in the sections that follow']
              }
            ]
          },
          {
            categoriesTitle: 'Available Reports',
            categoriesUrl: 'available-reports',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The following report tabs appear when you open the report view for a property or group of properties:'
              },
              {
                type: BlockType.IMG,
                data: 'Available_Reports_1.png',
                imgSize: IMG_SIZE.L
              },
              {
                type: BlockType.TEXT,
                data: 'By default the Property Detail report is open, but you can tab through the list to view other reports for the current property. Each report is described below:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Property Detail: View a comprehensive snapshot of a property characteristics, ownership information, sales history, and much more.',
                  'Comparables: Locate other properties that are similar to the property you are viewing to use in support of a list price recommendation.',
                  'Neighbors: Find all the neighbors located within a user-specified radius of a property.',
                  'Neighborhood Profile: Take an in-depth look at the neighborhood for the property, including the people (population), the housing statistics, quality of life information, and schools and other areas of interest.',
                  'Foreclosure: View foreclosure status and details, if the property is currently in any stage of foreclosure.',
                  'Assessor Map: If an Assessor Map of the property is available you can view the Map in a separate Map viewer tool.',
                  'Document Images: If any document images related to the property are available you can view the images in a special image viewer tool.',
                  'Flood Map: If a Flood Map of the areas is available you can view the Map in a separate Flood Map viewer tool.'
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'Each report is described in detail in the sections that follow.'
              },
              {
                type: BlockType.TEXT,
                data: '<b>NOTE:</b> Reports and default report settings can be customized by your MLS Administrator, so all reports may not appear exactly as they are described in this user guide.'
              },
              {
                type: BlockType.TITLE,
                data: 'Configuring Reports'
              },
              {
                type: BlockType.TEXT,
                data: 'The Property Detail, Comparables, Neighborhood Profile, Neighbors, and Market Trends reports have options that you can configure in your user preferences. Each report description includes the options you can configure.'
              }
            ]
          },
          {
            categoriesTitle: 'Property Detail Report',
            categoriesUrl: 'property-detail-report',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The Property Detail report is a comprehensive report that summarizes all the information related to the property, including owner, location, tax, history, property characteristics, and listing information. Information is categorized by owner information, location information, tax information, assessment and tax, characteristics, market sale and sale history, listing data, mortgage history, foreclosure history, owner transfer, and features.'
              },
              {
                type: BlockType.IMG,
                data: 'Property_Detail_Report_1.png',
                imgSize: IMG_SIZE.M
              }
            ]
          },
          {
            categoriesTitle: 'Comparables',
            categoriesUrl: 'comparables',
            data: [
              {
                type: BlockType.TEXT,
                data: 'Run this “Comps” report to find other properties that are similar to the property you are viewing. This is helpful for your listing and pricing activities. For this report, you can even toggle between Map views of the comparable properties, finding the one that best presents the information you want to communicate in your report. Running this report is a two step process.'
              },
              {
                type: BlockType.TITLE,
                data: 'Step One: Review the Search Results Properties'
              },
              {
                type: BlockType.TEXT,
                data: 'First, you run the report and review the properties that are returned:'
              },
              {
                type: BlockType.IMG,
                data: 'Comparables_1.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'Step Two: Refine the List'
              },
              {
                type: BlockType.TEXT,
                data: 'If your search returned a large number of comparable properties, you can work with the results right in the report to refine the comps you’ll generate on the final report. You can:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Review and deselect any properties you don’t want to include OR',
                  'You can click Search Criteria to change the criteria that were used to run the initial report and regenerate new output (see next section for details on report preferences).'
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'In the screen shot below, several properties have been removed from the list:'
              },
              {
                type: BlockType.IMG,
                data: 'Comparables_2.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'Step Three: View the Report'
              },
              {
                type: BlockType.TEXT,
                data: 'When you are ready to view the final report, click <b>Generate Report</b>. The Comparables report opens:'
              },
              {
                type: BlockType.IMG,
                data: 'Comparables_3.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'Configuring the Comparables Report'
              },
              {
                type: BlockType.TEXT,
                data: 'You can configure two areas of setting for the Comparables report:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  '<b>Report Format:</b> You can select the number of comparable properties that will appear, whether to include a summary of the comparable property statistics, the sort method, and whether or not to display the Map and pictures of the comparable property.',
                  '<b>Search Criteria:</b> You can select from a full set of criteria that will control what comparable properties will be returned in the report results.',
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'To configure the report:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['Click <b>Preferences</b> on the Realist taskbar:']
              },
              {
                type: BlockType.IMG,
                data: 'Comparables_4.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'The Preferences window opens. Click Reports and then Comparable Property Reports OR',
                  'Click the Configure Preferences link within a Comparables report.',
                  'The Comparables Report Preferences open:'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'Comparables_5.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Select the preferences for the report.',
                  'Click Save Changes to save and apply the changes and close the preferences window'
                ]
              }
            ]
          },
          {
            categoriesTitle: 'Neighbors',
            categoriesUrl: 'neighbors',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The Neighbors report lets you find all the neighbors located within a specified radius of a property. This report can be provided to prospective owners who would like to see who their neighbors are going to be. Neighbor searches are also an ideal way to prepare mailing lists for just-listed and just-sold cards.The Neighbors report gives basic details about properties near the subject property. Search parameters are defined by the preferences set by the user, including whether to limit results to properties on the subject property’s street.'
              },
              {
                type: BlockType.IMG,
                data: 'Neighbors_1.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'Configuring the Neighbors Report'
              },
              {
                type: BlockType.TEXT,
                data: 'You can configure two areas of settings for the Neighbors report:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Report Format: You can select the number of neighbor properties that will appear, and whether or not to display the Map and pictures of the neighbor properties.',
                  'Search Criteria: You can select the radius, in miles, around the property that neighbor properties will be returned. you can also limit the search to neighbors on the street.'
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'To configure the report:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['Click Preferences on the Realist taskbar:']
              },
              {
                type: BlockType.IMG,
                data: 'Neighbors_2.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'The Preferences window opens. Click Reports and then Neighbors Reports OR',
                  'At the top of the report itself, click the Customize View link:',
                ]
              },
              {
                type: BlockType.IMG,
                data: 'Neighbors_3.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['The Neighbors Report Preferences window opens:']
              },
              {
                type: BlockType.IMG,
                data: 'Neighbors_4.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['Select the preferences for the report:']
              },
              {
                type: BlockType.TABLE,
                data: [
                  ['Option', 'Description'],
                  ['Number of Neighbors', 'Enter the number of neighbor properties you would like to display on the report.'],
                  ['Distance from Subject', 'Enter the number of miles from the subject property to search for and display neighbors.'],
                  ['Geographic Boundary', 'Select whether to display properties just on the subject’s street or within the entire area.'],
                ]
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Click Save and Submit to save your changes and submit the changes for the current report OR',
                  'Click Submit to submit the changes for the current report but leave the original preferences for future reports'
                ]
              }
            ]
          },
          {
            categoriesTitle: 'Neighborhood Profile',
            categoriesUrl: 'neighborhood-profile',
            data: [
              {
                type: BlockType.TEXT,
                data: 'This report provides you with an in-depth look at the neighborhood of the property, including the people (population), the housing statistics, quality of life information, schools, and other areas of interest.T he Neighborhood report includes three major sections – Demographics, Schools, and Local Businesses. Each section uses a different geographical area as a basis for the search parameters.'
              },
              {
                type: BlockType.TEXT,
                data: 'This report is intended to be used as a local area report – focusing on a mile area. If you select a larger area, the amount of information the report will return can become overwhelming. For example, if you search for local businesses in a five mile area of an urban area, you will get hundreds of results displaying.'
              },
              {
                type: BlockType.TITLE,
                data: 'Demographics'
              },
              {
                type: BlockType.TEXT,
                data: 'The Demographic data comes from the Census Bureau and is defined by Census Track and Block. It includes 4 sub-sections – Population, Housing, Quality of Life, and Education:'
              },
              {
                type: BlockType.IMG,
                data: 'Neighborhood_Profile_1.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'Schools'
              },
              {
                type: BlockType.TEXT,
                data: 'The Schools section of the report lists the public and private schools within a specified geographic area (for example, in the sample below the area is a one mile radius). You can configure the geographic area in the Neighborhood Profiles report preferences.'
              },
              {
                type: BlockType.IMG,
                data: 'Neighborhood_Profile_2.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'Local Businesses'
              },
              {
                type: BlockType.TEXT,
                data: 'This section of the report lists local businesses within a specified geographic area (for example, in the sample below the area is a one mile radius. You can configure the geographic area in the Neighborhood Profiles report preferences.'
              },
              {
                type: BlockType.IMG,
                data: 'Neighborhood_Profile_3.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'Configuring the Neighborhood Profiles Report'
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['Click Preferences on the Realist taskbar:']
              },
              {
                type: BlockType.IMG,
                data: 'Neighborhood_Profile_4.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'The Preferences window opens. Click e Reports and then Neighborhood Profiles Report.0, The Neighborhood Profiles report preferences open.',
                  'Select which sections you’d like to include on the report:',
                  'Include Demographics',
                  'Include Schools (here you can also specific the distance from the subject property, the specific grades you are looking for, and the specific type of school (public, private, or both)',
                  'Include Businesses (here you can also specify the distance from the subject property and the category of business (e.g., banking, childcare, arts, etc.)',
                  '<b>NOTE</b>: You must select at least one option',
                  'Click Submit to save the changes and close the preferences window'
                ]
              }
            ]
          },
          {
            categoriesTitle: 'Premium Neighborhood Reports',
            categoriesUrl: 'premium-neighborhood-reports',
            data: [
              {
                type: BlockType.TITLE,
                data: 'Introducing Realist Premium Neighborhood Reports'
              },
              {
                type: BlockType.TEXT,
                data: 'Unlock the essence of every neighborhood with Realist Premium Neighborhood Reports, a suite of exclusive reports powered by NeighborhoodScout. Delve into unmatched data covering every aspect of your community, from real estate dynamics to demographic trends, educational opportunities, and future forecasts. With our comprehensive reports, you\'ll gain unparalleled market intelligence to make informed decisions tailored to your needs.'
              },
              {
                type: BlockType.TEXT,
                data: 'Each Premium Neighborhood report (Real Estate Data, Economics & Demographics, Schools Data, Trends & Forecasts) can be purchased for $9.99 by clicking on the “Premium Neighborhood Report” tab and then selecting the report of your choice. Each report can then be purchased  by using the same shopping cart and checkout process previously implemented for the Realist premium products.'
              },
              {
                type: BlockType.IMG,
                data: 'premium_neighborhood_reports_01.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.LIST_NUMBERS,
                data: [
                  '<b>Real Estate Data:</b>',
                ],
                startAt: 1
              },
              {
                type: BlockType.TEXT,
                data: 'Gain a competitive edge in the market with our Real Estate Data Report. Dive deep into key metrics such as median home values, market trends, rental rates, and neighborhood characteristics. Whether you\'re assisting clients in buying, selling, or investing, our report equips you with the insights needed to make informed decisions and stand out from the competition.'
              },
              {
                type: BlockType.TEXT,
                data: '<b>Key Data Points Include:</b>'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Median Home Value: Gain a clear understanding of the value of properties in your area.',
                  'Median Real Estate Taxes: Stay informed about tax trends affecting homeownership costs.',
                  'Neighborhood Home Prices Breakdown: Explore detailed pricing insights tailored to your specific area.',
                  'Homeownership Rate: Understand the percentage of residents who own their homes in your neighborhood',
                  'Average Market Rent: Get a pulse on rental rates to inform your client\'s investment decisions.',
                  'Gross Rental Yield: Evaluate the potential return on investment for rental properties.',
                  'Neighborhood Setting and Look & Feel: Discover the unique character and ambiance of your neighborhood.',
                  'Types of Homes and Special Purpose Housing: Explore the diverse housing options available in your area.',
                  'Home Size and Age of Homes: Understand the composition and age distribution of properties in your neighborhood.'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'premium_neighborhood_reports_02.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 2,
                data: [
                  '<b>Economics & Demographics:</b>',
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'As a real estate agent, your ability to understand and communicate the economic and demographic landscape of your community directly impacts your clients\' buying and selling decisions. Here\'s how our Economics & Demographics Report empowers you to better serve your clients:'
              },
              {
                type: BlockType.TEXT,
                data: '<b>Key Insights Include:</b>'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Household Income Trends: Provide valuable insights into income trends, helping buyers understand affordability and guiding sellers on pricing strategies.',
                  'Employment Opportunities: Highlight thriving job sectors and employment opportunities, attracting relocating buyers and ensuring sellers understand the market\'s economic stability.',
                  'Educational Attainment Levels: Guide families to neighborhoods with top-rated schools and educational resources, ensuring buyer\'s long-term satisfaction with their investment.',
                  'Cultural Diversity: Showcase the cultural richness of different neighborhoods, helping buyers find communities that align with their values and preferences.',
                  'Market Affordability: Assist first-time buyers in navigating the market by offering insights into housing affordability relative to income levels and guiding sellers on competitive pricing strategies.',
                  'Population Dynamics: Anticipate future market trends and buyer demand by tracking population shifts and demographic changes, ensuring your clients\' investments align with long-term growth potential.'
                ]
              },
              {
                type: BlockType.IMG,
                imgSize: IMG_SIZE.M,
                data: 'premium_neighborhood_reports_03.png'
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 3,
                data: [
                  '<b>Schools Data:</b>',
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'Enhance your client\'s home search experience with our Schools Data Report. Dive into school quality ratings, test scores, district information, and demographic insights. Whether your clients are families with children or investors interested in school district performance, our report empowers you to provide tailored recommendations and valuable insights.'
              },
              {
                type: BlockType.TEXT,
                data: '<b>Key Insights Include:</b>'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'School Quality Ratings: Offer transparent information on school performance, helping buyers evaluate educational options and make informed decisions.',
                  'District Information: Provide an overview of school districts, including enrollment numbers and student-teacher ratios, to help families understand the educational environment.',
                  'Test Scores: Showcase student achievement levels in reading, math, and other subjects, empowering buyers to assess academic rigor and school effectiveness.',
                  'Demographic Insights: Highlight the diversity of student populations, allowing buyers to find communities that celebrate inclusivity and cultural richness.',
                  'Neighborhood School Selection: Assist families in finding neighborhoods with schools that meet their specific needs and preferences, ensuring a seamless transition into their new community.'
                ]
              },
              {
                type: BlockType.IMG,
                imgSize: IMG_SIZE.M,
                data: 'premium_neighborhood_reports_04.png'
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 4,
                data: [
                  '<b>Trends & Forecasts:</b>',
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'Stay ahead of the curve and anticipate market trends with our Trends & Forecasts Report. Explore emerging trends, future projections, and market forecasts spanning real estate, demographics, education, and economic indicators. From identifying up-and-coming neighborhoods to guiding clients on investment strategies, our report provides you with the foresight needed to thrive in a competitive market.'
              },
              {
                type: BlockType.TEXT,
                data: '<b>Key Insights Include:</b>'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Market Trends Analysis: Gain insights into emerging trends and market dynamics, allowing you to advise clients on the best timing for buying or selling.',
                  'Regional Growth Projections: Understand regional growth patterns and development trends, helping buyers identify areas with long-term appreciation potential.',
                  'Future Market Forecasts: Access forecasts for real estate, demographics, education, and economic indicators, enabling you to advise clients on strategic investments and positioning.',
                  'Neighborhood Appreciation Potential: Evaluate appreciation potential at the neighborhood level, guiding buyers towards areas poised for growth and helping sellers maximize their return on investment.',
                  'Population and Demographic Shifts: Anticipate changes in population demographics and buyer preferences, ensuring your clients\' investments align with future market demand.'
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'With Realist Premium Neighborhood Reports, you\'ll have the tools to exceed your clients\' expectations and drive success in your real estate business. Gain a comprehensive understanding of every neighborhood\'s essence and unlock new opportunities today.'
              },
              {
                type: BlockType.TEXT,
                data: 'Each report is available for just $9.99, giving you unparalleled value for your investment in your business\'s success.'
              },
              {
                type: BlockType.IMG,
                imgSize: IMG_SIZE.M,
                data: 'premium_neighborhood_reports_05.png'
              }
            ]
          },
          {
            categoriesTitle: 'Assessor Map',
            categoriesUrl: 'assessor-map',
            data: [
              {
                type: BlockType.TEXT,
                data: 'If there are Assessor Maps associated with the property, an Assessor Map tab appears in the Report Window. The available sheets of the Map appear on the page:'
              },
              {
                type: BlockType.IMG,
                data: 'Assessor_Map_1.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TEXT,
                data: 'To View an Assessor Map'
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['Click the link to open the page of the Map that you would like to view. The Map Viewer opens, displaying the Map.']
              },
              {
                type: BlockType.TEXT,
                data: '<b>NOTE:</b>  If this is the first time you have opened a Map, you may be asked to install the Map Viewer add-on. Agree to this to proceed.'
              },
              {
                type: BlockType.IMG,
                data: 'Assessor_Map_2.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['View the Map and manipulate it using the Map toolbar:']
              },
            ]
          },
          {
            categoriesTitle: 'Foreclosure',
            categoriesUrl: 'foreclosure',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The Foreclosure report includes the most recent foreclosure transaction details if a property is in any stage of foreclosure, along with the default mortgage information and foreclosure history.'
              },
              {
                type: BlockType.TEXT,
                data: 'The state of foreclosure (pre-foreclosure, auction, or REO) is indicated in the black address bar at the top of the report (the property below is color-coded with yellow indicating the property is in auction.'
              },
              {
                type: BlockType.TEXT,
                data: 'Note: The Default Mortgage Information section may change by state, while all other sections of the report contain the same data. The names of the fields may vary, however, due to whether or not the state is judicial or non-judicial when it comes to property foreclosures. For example, a judicial state would call borrowers in default “defendants” while a nonjudicial state would call them “borrowers.”'
              },
              {
                type: BlockType.IMG,
                data: 'Foreclosure_1.png',
                imgSize: IMG_SIZE.M
              }
            ]
          },
          {
            categoriesTitle: 'Premium Flood Maps Report',
            categoriesUrl: 'premium-flood-map-reports',
            data: [
              {
                type: BlockType.TITLE,
                data: 'Premium Flood Maps Report for Cotality Property Insights Platform'
              },
              {
                type: BlockType.TITLE,
                data: 'Exclusive flood risk information makes you the market expert.'
              },
              {
                type: BlockType.TEXT,
                data: 'Weather patterns are changing, and so is the risk of overland flooding. Areas once considered low risk may not be as safe as people think. And flash flood risk can vary significantly from one parcel to the next, making accurate, property-level risk assessments more important than ever.'
              },
              {
                type: BlockType.TEXT,
                data: 'Available to agents for the first time through Realist®, Premium Flood Maps go beyond the basic FEMA flood determination data to provide a comprehensive analysis of a property’s true flood risk. With Premium Flood Maps, agents can give clients property-level flood risk information available nowhere else, underscoring their value as a trusted advisor and local market expert.'
              },
              {
                type: BlockType.IMG,
                data: 'premium_flood_map_1.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TITLE,
                data: 'Add Premium Flood Map Report to Cart'
              },
              {
                type: BlockType.TEXT,
                data: 'To begin using Premium Flood Maps Report, you must search for the subject property in question and then proceed click on view property details report. From here you’ll be able to access the Flood Maps report by clicking on the “Flood Maps” report tab.  If your account has prior access to the Flood Maps report tab, you’ll notice that the report is now delivered with updated report content and visualization. Below is an example of the new user experience and interface.'
              },
              {
                type: BlockType.IMG,
                data: 'premium_flood_map_2.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TEXT,
                data: 'It’s important to note that there are two distinctive Flood Map reports that are now available:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['Standard Flood Map Report',
                  'Premium Flood Map Report'
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'Let’s dive into the differences between the two Flood Map products.'
              },
              {
                type: BlockType.TITLE,
                data: 'Standard Flood Maps'
              },
              {
                type: BlockType.TEXT,
                data: 'The Standard Flood Map report, if available for your account, is now transformed with new user interface and visualization. This report provides a property map and FEMA flood zone determination, determining whether a property is located IN or OUT of a federally-designated flood zone.'
              },
              {
                type: BlockType.TEXT,
                data: 'The Standard Flood Map report comes standard with the following report attributes and definitions:'
              },
              {
                type: BlockType.TABLE,
                data: [
                  ['Term','Definition'],
                  ['Special Flood Hazard Area (SFHA)','Indicates whether the property location is In or Out of a Special Flood Hazard Area (100-Year floodplain).'],
                  ['County','Returns the county which the property is located in.'],
                  ['Community Participation Status','The National Flood Insurance Program (NFIP) status of community in the declared counties.'],
                  ['Original Panel Firm Date','Effective date of the map.'],
                  ['Distance to 100 yr Flood Plain','Distance in feet between the property and the boundary of the 100-year flood zone located in the same catchment or subwatershed. If a 100-Year floodplain is not within the radius search, a value of -1 will be returned.'],
                  ['FIPS Code','The five-digital state and county FIPS code.'],
                  ['Community Number - Map Panel & Suffix','Two-to-four digit number and suffix assigned by FEMLA for the map panel.'],
                  ['Coastal Barrier Date','Returns date of CBRA classification, if available.'],
                  ['Flood Zone Code','Flood zone for the property location based on the FEMLA FIRM.'],
                  ['Community Name','Name of the community.'],
                  ['Panel Date','Date of the FEMA map panel.'],
                  ['Coastal Barrier Resource Area (CBRA)','Coastal Barrier Resource Act (CBRA) protects areas that serve as barriers against wind and tidal forces caused by coastal storms and serves as habitat for aquatic species. Returns In or Out, for identifying weather the property is located within a CBRA zone.'],
                  ['Letter of Map Amendment (LOMA)','A Letter of Map Amendment (LOMA) is an official amendment, by letter, to an effective National Flood Insurance Program (NFIP) map. A LOMA establishes a property\'s location in relation to the Special Flood Hazard Area (SFHA).'],
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'You will still have the same ability to download for printing or emailing of the Standard Flood Map report.'
              },
              {
                type: BlockType.TITLE,
                data: 'Premium Flood Map Report'
              },
              {
                type: BlockType.TEXT,
                data: 'The Premium Flood Map Report, if available for your account and available for the subject property in question, is an upgradable flood map report that provides flood risk assessment data provided in form of two scoring models from Cotality:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Cotality Flood Risk Score',
                  'Cotality Flash Flood Risk Score'
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'When viewing the Standard Flood Map report, simply click on the “Upgrade Flood Map Report” button at top to initiate the upgrade process.'
              },
              {
                type: BlockType.IMG,
                data: 'premium_flood_map_3.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TEXT,
                data: 'In addition to the Cotality Flood Risk Score and Cotality Flash Flood Risk Score, the Premium Flood Map Report is delivered with the following report attributes and definitions:'
              },
              {
                type: BlockType.TABLE,
                data: [
                  ['Term','Definition'],
                  ['Distance to 100 yr Flood Plain','Distance in feet between the property and the boundary of the 100-year flood zone located in the same catchment or subwatershed. If a 100-Year floodplain is not within the radius search, a value of -1 will be returned.'],
                  ['100 yr Plain Note','Note provided in absence of a distance.'],
                  ['Subwatershed Name','Returns the value of the subwatershed name.'],
                  ['Property Elevation','Returns the elevation, in feet, of the subject property.'],
                  ['Additional Comments','Returns additional comments or information.'],
                  ['Distance to 500 yr Flood Plain','Distance in feet between the property and the boundary of the 500-year flood zone. If a 500-Year floodplain is not found a value of -1 will be returned.'],
                  ['500 yr Plain Note','Note provided in absence of a distance.'],
                  ['Subwatershed Code','Returns the code for the subwatershed.'],
                  ['Water Surface Elevation','Provides the elevation, in feet, of the water surface.'],
                  ['Distance to Dam','Provides the distance, in feet, to dam.'],
                  ['Distance to Levee','Provides the distance, in feet, to levee.'],
                  ['Additional Impact Area','Returns notes for additional impact area.'],
                  ['Elevation Variance','Returns the elevation variance, in feet.']
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'Similarly to the Standard Flood Map Report, you will have the same ability to download the report for printing or emailing of the Standard Flood Map report.'
              },
              {
                type: BlockType.TITLE,
                data: 'Upgrade from Standard Flood Map to Premium Flood Map Report'
              },
              {
                type: BlockType.TEXT,
                data: 'When viewing the Standard Flood Map Report, you are given the option to upgrade to a Premium Flood Map (if available for your account – please contact your MLS administrator if you do not see the Flood Maps report tab in your account).'
              },
              {
                type: BlockType.TEXT,
                data: '<b>Steps to Upgrade:</b>'
              },
              {
                type: BlockType.LIST_NUMBERS,
                data: [
                  'Click on <b>Upgrade Flood Map Report</b>',
                  'Click on <b>Add to Cart</b> (Note: You can add multiple premium flood map reports to cart, for different properties, prior to checkout)',
                  'Click on <b>Shopping Cart</b> to proceed to <b>Checkout</b> page'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'premium_flood_map_4.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 4,
                data: [
                  'Once on the <b>Cart</b> page (below), you will see options to A) Remove product from shopping cart, B) Save cart, C) Add promo codes, if you have a valid promo to use should be entered into the <b>Promo Code</b> field and then select <b>Redeem</b> D) Proceed to checkout to enter credit card information'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'premium_flood_map_5.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 5,
                data: [
                  'On the Checkout page is where you A) Enter credit card information and bill address',
                  'Place secure order',
                  'Edit items to add or remove items from your cart'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'premium_flood_map_6.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'Access Your Premium Flood Map Report After Purchase'
              },
              {
                type: BlockType.TEXT,
                data: 'Order Confirmation Page'
              },
              {
                type: BlockType.TEXT,
                data: 'Once you have completed the purchase of the Premium Flood Map report, you’ll see an order confirmation page, similar to the one below. If you purchased multiple flood map reports during one transaction, each individual Premium Map Report will appear on the same Order Confirmation page. Each report will have its own “View Report” link, as indicated below.'
              },
              {
                type: BlockType.TEXT,
                data: 'Acknowledgement of Refund Policy'
              },
              {
                type: BlockType.TEXT,
                data: 'Because the Premium Flood Map report is delivered as a digital product, returns are not accepted once you have viewed or accessed the report content. In order to begin viewing the Premium Flood Map report content, you must acknowledge and accept the refund policy set forth by Cotality by clicking on the “Confirm” button.'
              },
              {
                type: BlockType.IMG,
                data: 'premium_flood_map_7.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TEXT,
                data: 'View Flood Risk & Flash Flood Risk, Print & Email Report'
              },
              {
                type: BlockType.TEXT,
                data: 'The Premium Flood Map Report can be viewed, printed, and emailed just like the other Realist reports. When you first access this report, the top section will display the Flood Zone Determination (which is available as part of the Standard Flood Map Report, which we’ve included in the Premium report. Once you scroll past the <b>Flood Zone Determination</b> section, the <b>Cotality Flood & Flash Flood Data</b> is represented on screen. To navigate between Flood and Flash Flood Data, there are selectable buttons available to switch between the two risk models. Both risk models also over a map imagery with zoom in/zoom out controls so that you can view the risk heat map at the desired zoom level.'
              },
              {
                type: BlockType.IMG,
                data: 'premium_flood_map_9.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TEXT,
                data: 'Printing and emailing of the report are also available. Simply navigate to the top right section of the report and click on “Print” or “Email” to initiate the printing/emailing of the report.'
              },
              {
                type: BlockType.IMG,
                data: 'premium_flood_map_10.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'Refund'
              },
              {
                type: BlockType.TEXT,
                data: 'Change your mind about a report you haven’t accessed or viewed to download or email? Simply click on the <b>Your Orders</b> report icon to navigate to the Order Summary page. This is where you are able to initiate a request for a return. For eligible reports, you can click on the <b>Return</b> button located to the far right of the report, select your reason for return, any optional comments, and then click on <b>Confirm Return</b> button. When a refund is requested, Cotality will issue the refund immediately; however, processing time for a refund will be between 5-10 days depending on your financial institution.'
              },
              {
                type: BlockType.IMG,
                data: 'premium_flood_map_11.png'
              }
            ]
          },
          {
            categoriesTitle: 'Hazards & Risks Report',
            categoriesUrl: 'hazards-risk-reports',
            data: [
              {
                type: BlockType.TITLE,
                data: 'The new Hazards & Risks report from Cotality®, provides property-level information for a variety of risk types:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                    'Earthquake—including probability, severity (shaking/damage), soil types, and spectral acceleration',
                    'Wildfire—based on a combination of brushfire data, proximity to high-risk areas, recent fires, and more',
                    'Wind & Storm—tornadic, straight-line, and hurricane event risk',
                    'Crime—neighborhood statistics for common property and violent crimes',
                    'Regional—such as hail, lava, tsunami, and sinkholes',
                    'Distance to Fire Station—an important first-responder metric',
                    'Roof Age—based on construction and permit data'
                  ]
              },
              {
                type: BlockType.TITLE,
                data: 'Add Hazards & Risks Report to Cart'
              },
              {
                type: BlockType.TEXT,
                data: 'To begin using Hazards & Risks Report, you must search for the subject property in question and then proceed click on view property details report. From here you’ll be able to access the Hazards & Risks report tab. Click on this tab to access the dedicated page setup to add the product to cart. Below is an example of the new user experience and interface to add the product to cart.'
              },
              {
                type: BlockType.IMG,
                data: 'hazard_map_002.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TITLE,
                data: 'Steps to Purchase:'
              },
              {
                type: BlockType.LIST_NUMBERS,
                data: [
                  'Click on Hazards & Risks Report tab',
                  'Click on Add to Cart (Note: You can add multiple Hazards & Risks reports to cart, for different properties, prior to checkout)',
                  'Click on Shopping Cart icon at top-left to proceed to Checkout page'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'hazard_map_003.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 4,
                data: [
                  'Once on the Cart page (example below), you will see the following options:'
                ]
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Remove product from shopping cart',
                  'Save cart',
                  'Add promo code, if you have a valid promo to use, should be entered into the Promo Code field and then select Redeem',
                  'Proceed to checkout to enter credit card information.'
                ]
              },
              {
                type: BlockType.IMG,
                imgSize: IMG_SIZE.M,
                data: 'hazard_map_004.png'
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 5,
                data: [
                  'On the Checkout page is where you:',
                ]
              }, {
                type: BlockType.LIST_DOTS,
                data: [
                  'Enter credit card information and bill address',
                  'Place secure order',
                  'Edit items to add or remove items from your cart'
                ]
              },
              {
                type: BlockType.IMG,
                imgSize: IMG_SIZE.M,
                data: 'hazard_map_005.png'
              },
              {
                type: BlockType.TITLE,
                data: 'Access Your Hazards & Risks Report After Purchase'
              },
              {
                type: BlockType.TITLE,
                data: 'Order Confirmation Page'
              },
              {
                type: BlockType.TEXT,
                data: 'Once you have completed the purchase of the Hazards & Risks Report you’ll see an order confirmation page, similar to the example below. If you purchased multiple reports during one transaction, each individual report will appear on the same Order Confirmation page.'
              },
              {
                type: BlockType.TEXT,
                data: 'Each report will have its own “View Report” link, as indicated below. Please note that due to the length of the Hazards & Risks report, we’ve also made the Glossary PDF a separate file for download. That file is accessible on the order confirmation page as well as the “Your Orders” page.'
              },
              {
                type: BlockType.IMG,
                imgSize: IMG_SIZE.M,
                data: 'hazard_map_006.png'
              },
              {
                type: BlockType.TITLE,
                data: 'Acknowledgement of Refund Policy'
              },
              {
                type: BlockType.TEXT,
                data: 'Because the Hazards & Risks Report is delivered as a digital product, returns are not accepted once you have viewed or accessed the report content. To begin viewing the Hazards & Risks Report content, you must acknowledge and confirm your understanding by clicking on the “Confirm” button.'
              },
              {
                type: BlockType.IMG,
                imgSize: IMG_SIZE.M,
                data: 'hazard_map_007.png'
              },
              {
                type: BlockType.TITLE,
                data: 'Understanding each risk profile'
              },
              {
                type: BlockType.TEXT,
                data: 'The Hazards & Risks report provides property-level information for a variety of risks. Please see below for more details on each risk profile, description, and rating.'
              },
              {
                type: BlockType.TITLE,
                data: 'Crime Risk Score'
              },
              {
                type: BlockType.TEXT,
                data: 'Cotality provides the most detailed national database of neighborhood crime statistics available today through Location Inc., a Cotality company. CrimeRisk™ data contain no Prohibited Basis - data about race, color, religion, national origin, sex & orientation, marital status, age, or disability. These crime scores and indexes are created using proprietary methodology. Indices have been built using a distance weighted cone of influence sampling methodology to more accurately portray reality. The database includes statistics for murder, aggravated assault, larceny, robbery, property crime, motor vehicle theft, rape, burglary, and overall total crimes.'
              },
              {
                type: BlockType.TABLE,
                data: [
                  ['Risk Score','Risk Description'],
                  ['Very Low Risk (0-10)','Crime Risk half the national average or lower'],
                  ['Low Risk (11-20)','Crime Risk half the national average to the national average'],
                  ['Moderate Risk (21-40)','Crime Risk from the national average to twice the national average'],
                  ['High Risk (41-60)','Crime Risk from two to three times the national average '],
                  ['Very High Risk (61-80)','Crime Risk from three to four times the national average'],
                  ['Extreme Risk (81-99)','Crime Risk from four to five times the national average'],
                  ['Very Extreme Risk (100)','Crime Risk five times the national average or higher']
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Earthquake Risk Score'
              },
              {
                type: BlockType.TEXT,
                data: 'Earthquake Risk Score predicts a location’s risk for earthquake damage, along with the potential earthquake severity. Earthquake Risk Score incorporates the most current earthquake science and data available to produce a probabilistic risk model covering the entire United States. The information is intended to serve as an indicator of the extent of ground shaking that could occur at a location in the event of seismic activity. Our earthquake risk score is derived from our proprietary model that incorporates a comprehensive view of how soil type affects ground shaking. The model combines two factors to determine an overall earthquake risk score – ranging from Very Low to Very High risk; normalized to give a useful scoring system from 1-100; where 100 is the highest risk.'
              },
              {
                type: BlockType.TABLE,
                data: [
                  ['Risk Score','Risk Description'],
                    ['1-19','Very Low'],
                    ['20-24','Low'],
                    ['25-60','Moderate'],
                    ['61-80','High'],
                    ['81-100','Very High']
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Wind & Storm Risk Score'
              },
              {
                type: BlockType.TEXT,
                data: 'Wind Risk Score provides information about a locations risk score (1-100) for each of: tornadic, straight-line and hurricane wind risk events.'
              },
              {
                type: BlockType.TITLE,
                data: 'Wildfire Risk Score'
              },
              {
                type: BlockType.TEXT,
                data: 'This report provides information about a property location’s wildfire risk. Cotality’s Wildfire Risk Score data utilizes a combination of brushfire data and proximity to higher risk areas and Wildlands to provide an accurate assessment of a property’s wildfire exposure to risk on the property and from firebrands. Since fuels are often consumed by a fire, recent fire activity may lower the Risk Description and the Risk Score within the perimeter of a recent fire. This reduction in risk is calculated into the risk score in the year following the fire but is only temporary. As the fuels regrow the risk will return to its original pre-fire value.'
              },
              {
                type: BlockType.TABLE,
                data: [
                  ['Risk Score','Risk Description'],
                  ['Low (0-50)','Minimal wildfire threat. Possible ember ignitions based on more distant fuels.'],
                  ['Moderate (51-60)','Increased wildfire threat due to closer proximity to ember sources.'],
                  ['High (61-80)','Elevated wildfire threat. Ember ignitions from nearby or adjacent fuel sources or possible higher risk fuels on the property.'],
                  ['Very High (81-100)','Extreme wildfire threat. Not only embers from nearby fuels but likely higher risk fuels on the property or in very close proximity']
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Regional Risks'
              },
              {
                type: BlockType.TITLE,
                data: 'Tsunami '
              },
              {
                type: BlockType.TEXT,
                data: 'The Tsunami Risk shows you planned areas of evacuation in the event of tsunami. This Feature covers the states of California, Hawaii, Oregon, and Washington. It is based on source data available from the Pacific Disaster Center, the National Geophysical Data Center, the Washington Department of Natural Resources, and the California Department of Conservation.'
              },
              {
                type: BlockType.TITLE,
                data: 'Hailstorm Risk '
              },
              {
                type: BlockType.TEXT,
                data: 'This report provides a Hail Claim Score (1-100) based on the probability of a hail claim being submitted for a property location for both Personal Lines and Commercial Lines properties at the census block level. In addition, a Hailstorm Risk Score (1-100) of 1” hail stones impacting a property location is provided along with the annual probability of 0.75” hail stones, 1.0” inch hail stones, 1.5” hail stones, and greater than 2” hail stones. Utilizing Cotality Hail Verification Technology, Storm Prediction Data, and North American Regional Reanalysis data we have created a 20km x 20km grid for the continental U.S to identify the annual probability for hail risk at distinct sizes. '
              },
              {
                type: BlockType.TITLE,
                data: 'Sinkhole Risk '
              },
              {
                type: BlockType.TEXT,
                data: 'This report uses ground composition data, a comprehensive database of known sinkholes, and a proprietary methodology developed by a team of PhD-level scientists that analyzes soil and other factors that contribute to catastrophic ground collapse. In addition to risk level, the Sinkhole Integrated report also provides distance and status of sinkholes within user defined distances from the subject property. This comprehensive report is a great way for users to gain more insight into sinkhole risk.'
              },
              {
                type: BlockType.TITLE,
                data: 'Lava '
              },
              {
                type: BlockType.TEXT,
                data: 'Provides lava flow hazard zone information for the large island of Hawaii. The lava flow hazard zones depicted in this were developed for the five volcanoes on the Island of Hawaii by the United States Geological Survey (USGS) and Hawaii Volcanoes Observatory.'
              },
              {
                type: BlockType.TITLE,
                data: 'Distance to fire station'
              },
              {
                type: BlockType.TEXT,
                data: 'Provides the distance, in feet, to the three nearest fire stations.'
              },
              {
                type: BlockType.TITLE,
                data: 'Roof Age'
              },
              {
                type: BlockType.TEXT,
                data: 'This report uses building permit data to determine the age of a roof. It can be used for both homes and commercial properties. If no permit is found, the report uses other data such year build to estimate the age of the roof.'
              }
            ]
          },
          {
            categoriesTitle: 'Downloading and Emailing Reports',
            categoriesUrl: 'downloading-and-emailing-reports',
            data: [
              {
                type: BlockType.TEXT,
                data: 'A PDF version of the report can be generated to download or email'
              },
              {
                type: BlockType.TEXT,
                data: 'To Download or Email a Report'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Select a property and open the Report Window for the report.',
                  'Click the Download or Email button at the bottom of the page:',
                ]
              },
              {
                type: BlockType.IMG,
                data: 'Downloading_and_Emailing_Reports_1.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.IMG,
                data: 'Downloading_and_Emailing_Reports_2.png',
                imgSize: IMG_SIZE.M
              },
              {
                type: BlockType.TEXT,
                data: 'SELECT one OF the FOLLOWING OPTIONS FROM Download OR Email menu:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  '<b>Quick Download/Quick Email:</b> Select this option to generate a PDF version of the current report. For example, if you are viewing the Property Detail Report or Neighbors report and you select <b>Download > Quick Download</b>, a PDF version of the Property Detail Report or Neighbors report will be generated, which you can then print.',
                  '<b>Customize Download/Customize Email:</b> Select this option to select the reports that you would like to include in the PDF. For example, if you are viewing the Neighbors report and you select <b>Download > Customize Download</b>, a window will open where you can select exactly what you want to appear in the PDF (by default, all reports are selected to appear in the PDF):',
                ]
              },
              {
                type: BlockType.IMG,
                data: 'Downloading_and_Emailing_Reports_3.png',
                imgSize: IMG_SIZE.S
              },
              {
                type: BlockType.TEXT,
                data: '<b>TIP:</b> The Customize window will remember your settings from the previous time you downloaded or emailed the custom report.'
              }
            ]
          },
          {
            categoriesTitle: 'Building Permits Report',
            categoriesUrl: 'building-permits-report',
            data: [
              {
                type: BlockType.TITLE,
                data: 'Introducing Realist’s New Premium Building Permits Report Powered by BuildZoom'
              },
              {
                type: BlockType.TEXT,
                data: 'We’re thrilled to announce Realist’s latest premium feature—the <b>Building Permits Report</b>, powered by BuildZoom! This powerful new tool gives you direct access to one of the most comprehensive building permit databases available, helping you provide even greater property insights to your clients.'
              },
              {
                type: BlockType.TEXT,
                data: 'Available for just <b>$9.99 per report</b>, this feature delivers detailed information on property improvements, giving you the transparency and confidence to ensure your clients are making well-informed decisions.'
              },
              {
                type: BlockType.TITLE,
                data: 'Key Features of the Premium Building Permits Report:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  '<b>Extensive Permit Data</b>: Access over 175 million building permits for residential properties, giving you the details you need on property renovations and improvements.',
                  '<b>Contractor Information</b>: Get data on over 4 million licensed contractors, including job values and permit numbers, so you know the work was done by qualified professionals.',
                  '<b>Property-Level Insights</b>: Generate deep, property-specific reports that offer a full understanding of work done on a property, from small updates to major renovations.',
                ]
              },
              {
                type: BlockType.TITLE,
                data: 'Why It’s a Game-Changer for Agents:',
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  '<b>Build Trust with Clients</b>: Having building permit details at your fingertips gives you an edge, offering transparency that helps build trust and credibility with your buyers.',
                  '<b>Answer Client Questions</b>: With detailed permit and contractor data, you can offer tailored insights that address client concerns about past work or future risks.',
                  '<b>Provide Informed Guidance</b>: This report equips you with critical property history, making it easier to guide your clients through one of their most important decisions.',
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'The Realist Building Permits Report is designed to help you stand out by delivering crucial property details your clients can rely on. Take advantage of this premium feature and offer added value with insights that truly set you apart.',
              },
              {
                type: BlockType.TITLE,
                data: 'How to Access Realist’s Premium Building Permits Report',
              },
              {
                type: BlockType.TEXT,
                data: 'Follow these simple steps to unlock the full potential of Realist’s new Premium Building Permits Report powered by BuildZoom. Each report is available for <b>$9.99</b> and provides detailed insights into property renovations and contractor information.'
              },
              {
                type: BlockType.IMG,
                data: 'Building_Permits_Report_1.png',
                imgSize: IMG_SIZE.M,
              },
              {
                type: BlockType.TITLE,
                data: 'Step-by-Step Guide:',
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 1,
                data: [
                  '<b>Search for the Subject Property</b>',
                ],
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Log in to Realist and start by entering the address of the property you want to investigate into the Quick Search or My Search bar. Once the property is located, click on it to view the property details.',
                ],
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 2,
                data: [
                  '<b>Select the "Building Permits Report" Tab</b>',
                ],
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'At the top right of the property’s detail page, you’ll see the option labeled “Building Permits Report.” Click on this tab to access the premium report option.',
                ],
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 3,
                data: [
                  '<b>Add to Cart</b>'
                ],
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Once you’re on the Building Permits Report page, click the button that says <b>"Add to Cart"</b>. This will initiate the checkout process.',
                ],
              },
              {
                type: BlockType.IMG,
                data: 'Building_Permits_Report_2.png',
                imgSize: IMG_SIZE.S,
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 4,
                data: [
                  '<b>Proceed to Checkout</b>',
                ]
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Navigate to your cart and click <b>“Checkout.”</b> You’ll be prompted to enter your payment information to complete the purchase. Each report is available for <b>$9.99</b>.',
                ],
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 5,
                data: [
                  '<b>Complete Payment</b>',
                ],
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Enter your credit card information and review your order. Once you’re ready, submit the payment.',
                ],
              },
              {
                type: BlockType.LIST_NUMBERS,
                startAt: 6,
                data: [
                  '<b>Access Your Report</b>',
                ],
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Upon successful payment, your Building Permits Report will be available for viewing and download. You can now access the comprehensive permit data and contractor information to better serve your clients.',
                ],
              },
              {
                type: BlockType.IMG,
                data: 'Building_Permits_Report_3.png',
                imgSize: IMG_SIZE.M,
              },
            ]
          },
          {
            categoriesTitle: 'Document Image',
            categoriesUrl: 'document-image',
            data: [
              {
                type: BlockType.TITLE,
                data: 'Introducing Revised Ordering Process For Document Image in Realist',
              },
              {
                type: BlockType.TEXT,
                data: 'There is now a revised process to order <b>Document Image</b> in Realist. The Document Image feature in Realist allows agents to access property-related documents (e.g. deeds, mortgages, and liens) quickly and efficiently.',
              },
              {
                type: BlockType.TEXT,
                data: 'Follow the steps below to order a Document Image:',
              },
              {
                type: BlockType.TITLE,
                data: 'Step 1: Search for a Property',
              },
              {
                type: BlockType.LIST_NUMBERS,
                data: [
                  'Enter the address, parcel ID, or owner name in the search bar.',
                  'Review the search results and click on the desired property to view detailed information.',
                ],
              },
              {
                type: BlockType.TITLE,
                data: 'Step 2: Locate the Document Image Section',
              },
              {
                type: BlockType.LIST_NUMBERS,
                data: [
                  'Scroll down to the <b>Property Details</b> page.',
                  'Find the <b>Document Images</b> section, typically located near the <b>Last Market Sale & Sales History</b> section.',
                  'Select a document to order by clicking on the hyperlinked document number.',
                ],
              },
              {
                type: BlockType.IMG,
                data: 'Document_Image_1.png',
                imgSize: IMG_SIZE.M,
              },
              {
                type: BlockType.TITLE,
                data: 'Step 3: Select Add to Cart on the Unlock Document Image Window',
              },
              {
                type: BlockType.LIST_NUMBERS,
                data: [
                  'The Unlock Document Image window will open with the option to <b>Add to Cart</b> for the desired document.'
                ]
              },
              {
                type: BlockType.IMG,
                data: 'Document_Image_2.png',
                imgSize: IMG_SIZE.M,
              },
              {
                type: BlockType.TITLE,
                data: 'Step 4: Cart Page/Review Your Order',
              },
              {
                type: BlockType.LIST_NUMBERS,
                data: [
                  'Navigate to your <b>Cart</b> (click on the cart icon at top navigation bar).',
                  'Review the selected Document Image total charges or credits required.',
                ]
              },
              {
                type: BlockType.IMG,
                data: 'Document_Image_3.png',
                imgSize: IMG_SIZE.M,
              },
              {
                type: BlockType.TITLE,
                data: 'Step 5: Proceed to Checkout',
              },
              {
                type: BlockType.LIST_NUMBERS,
                data: [
                  'Click <b>Proceed To Checkout</b> to confirm your purchase.',
                  'Follow the on-screen instructions to:',
                ]
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Enter payment details',
                  'Proceed through the checkout process',
                ]
              },
              {
                type: BlockType.IMG,
                data: 'Document_Image_4.png',
                imgSize: IMG_SIZE.M,
              },
              {
                type: BlockType.TITLE,
                data: 'Step 6: Access Your Document',
              },
              {
                type: BlockType.LIST_NUMBERS,
                data: [
                  'Once the order is confirmed, the document will be available for download. Simply click on <b>View Report</b> to access the document.',
                  'You can also access the <b>report</b> in <b>Your Orders</b> page at the top navigation.',
                ],
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Ensure you have a PDF viewer installed to open the document.'
                ],
              },
              {
                type: BlockType.IMG,
                data: 'Document_Image_5.png',
                imgSize: IMG_SIZE.M,
              }
            ]
          }
        ]
      },
    ]
  },
  {
    title: 'Tutorials',
    url: RootUserGuideRoutes.Tutorials,
    pageContent: [
      {
        pageTitle: '',
        categories: [
          {
            categoriesTitle: 'Intro to Realist',
            categoriesUrl: 'intro-to-realist',
            data: [
              {
                type: BlockType.TEXT,
                data: 'Welcome to Realist! <br/> You are ready to get started, and the good news is that Realist is an all-in-one solution that will put the tools you need to do your job better, at your fingertips. '
              },
              {
                type: BlockType.TEXT,
                data: 'This video provides general information about Realist:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'What is Realist? Tax & Public Records with integrated MLS data',
                  'Integration with MLS Platforms – how do I access it?',
                  'What is contained within it? <br/> Sales, Foreclosures, Mortgages, Taxes & Assessments (and more!)',
                  'Overview of home page and customization options'
                ]
              },
              {
                type: BlockType.VIDEO,
                data: 'https://www.youtube.com/embed/XxW7Isr8we4'
              }
            ]
          },
          {
            categoriesTitle: 'Using Search',
            categoriesUrl: 'using-search',
            data: [
              {
                type: BlockType.TEXT,
                data: 'Realist Search is a powerful, fully configurable search tool that offers you the ability to search using a wide-range of search criteria, including things like number of bedrooms, lot size, APN/Folio/Tax ID (subject to change by your MLS), and Owner, to name just a few. '
              },
              {
                type: BlockType.TEXT,
                data: 'This short video tutorial provides a guidance on using the Search tool in Realist:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Selecting Counties',
                  'Quick Search vs My Search',
                  'How to create new or add to existing search templates',
                  'Admin Searches vs Search Templates vs. Search Templates with values',
                  'Saving Searches',
                  'Executing Searches',
                  'Count on the fly',
                  'View 1-1000, 1001-2000 and 2001-3000 results',
                ]
              },
              {
                type: BlockType.VIDEO,
                data: 'https://www.youtube.com/embed/4qMKcefFOe0'
              }
            ]
          },
          {
            categoriesTitle: 'Search Results Grid',
            categoriesUrl: 'search-results-grid',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The Search feature is designed to be flexible enough to support the variety of tasks you may need to complete during your workday.'
              },
              {
                type: BlockType.TEXT,
                data: 'This video provides specific ways you can work with your search results:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Adding/Removing/Ordering columns and sorting',
                  'Integration between map and properties checked in grid',
                  'Card View & Customization favorites',
                  'Download/Email: Quick vs Customize',
                  'High level overview of Labels/Export',
                ]
              },
              {
                type: BlockType.VIDEO,
                data: 'https://www.youtube.com/embed/fcEUqBmIYjs'
              }
            ]
          },
          {
            categoriesTitle: 'Property Detail Report',
            categoriesUrl: 'property-detail-report',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The Property Detail report is a comprehensive report that summarizes all the information related to the property, including owner, location, tax, history, property characteristics, and listing information. Information is categorized by owner information, location information, tax information, assessment and tax, characteristics, market sale and sale history, listing data, mortgage history, foreclosure history, owner transfer, and features.'
              },
              {
                type: BlockType.IMG,
                data: 'Property_Detail_Report_1.png',
                imgSize: IMG_SIZE.M
              }
            ]
          },
          {
            categoriesTitle: 'Map Tools',
            categoriesUrl: 'map-tools',
            data: [
              {
                type: BlockType.TEXT,
                data: 'The Map tools allow you to customize the Map to give you a better perspective on a search area. This gives you the ability to visually scan important information about a property and its surrounding areas and also easily target properties in specific geographic areas.'
              },
              {
                type: BlockType.TEXT,
                data: 'In this video you will learn how to use:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Shape Drawing',
                  'Boundaries',
                  'Property & Sales Info: Distressed Sales. Tax Sales/Pass AVM. MLS Status. Characteristics',
                  'Trends',
                  'Points of Interest',
                  'Driving Directions',
                  'Find on Map',
                  'Measure',
                  'Annotate',
                ]
              },
              {
                type: BlockType.VIDEO,
                data: 'https://www.youtube.com/embed/9KGhcb_a3to'
              }
            ]
          },
          {
            categoriesTitle: 'Labels & Exports',
            categoriesUrl: 'labels-exports',
            data: [
              {
                type: BlockType.TEXT,
                data: 'In this short tutorial you will learn how to:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Do a Shape Search',
                  'Export to labels: Label Formats. Owner Mailing vs Property Address. Customizing Labels',
                  'Export to CSV (Quick vs Customize)',
                ]
              },
              {
                type: BlockType.VIDEO,
                data: 'https://www.youtube.com/embed/7CQowhV_mBc'
              }
            ]
          },
          {
            categoriesTitle: 'Other Reports',
            categoriesUrl: 'other-reports',
            data: [
              {
                type: BlockType.TEXT,
                data: 'This short video tutorial provides the guidance how to work with:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Comparable Report',
                  'Flood Map',
                  'Neighbors',
                  'Assessor Map',
                  'Foreclosure',
                ]
              },
              {
                type: BlockType.VIDEO,
                data: 'https://www.youtube.com/embed/fVNshV6jJ34'
              }
            ]
          },
          {
            categoriesTitle: 'Premium Map Layers',
            categoriesUrl: 'premium-map-layer',
            data: [
              {
                type: BlockType.TEXT,
                data: 'This short video tutorial provides the guidance how to work with:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['Premium Map Layers']
              },
              {
                type: BlockType.VIDEO,
                data: 'https://bcove.video/400y700'
              }
            ]
          },
          {
            categoriesTitle: 'Sell Score',
            categoriesUrl: 'sell-score',
            data: [
              {
                type: BlockType.TEXT,
                data: 'This short video tutorial provides the guidance how to work with:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['Sell Score']
              },
              {
                type: BlockType.VIDEO,
                data: 'https://www.youtube.com/embed/0BMNSs1ZGyM'
              }
            ]
          },
          {
            categoriesTitle: 'Premium Flood Maps',
            categoriesUrl: 'premium-flood-maps',
            data: [
              {
                type: BlockType.TEXT,
                data: 'This short video tutorial provides the guidance how to work with:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['Premium Flood Maps']
              },
              {
                type: BlockType.VIDEO,
                data: 'https://bcove.video/3BEDl8j'
              }
            ]
          },
          {
            categoriesTitle: 'Hazards & Risks Report',
            categoriesUrl: 'hazards-&amp;-risks-report',
            data: [
              {
                type: BlockType.TEXT,
                data: 'This short video tutorial provides the guidance how to work with:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['Hazards & Risks Report']
              },
              {
                type: BlockType.VIDEO,
                data: 'https://bcove.video/4glQVN5'
              }
            ]
          },
          {
            categoriesTitle: 'Community Insight and Rental Trends',
            categoriesUrl: 'community-insight-and-rental-trends',
            data: [
              {
                type: BlockType.TEXT,
                data: 'This short video tutorial provides the guidance how to work with:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: ['Community Insight', 'Rental Trends']
              },
              {
                type: BlockType.VIDEO,
                data: 'https://bcove.video/4iI93lB',
              }
            ]
          },
          {
            categoriesTitle: 'Insights Dashboard',
            categoriesUrl: 'insights-dashboard',
            data: [
              {
                type: BlockType.TEXT,
                data: 'Welcome to the Realist Insights Dashboard, where you can harness the power of market intelligence and streamline your real estate endeavors. This comprehensive tutorial video will guide you through each feature, outlining their benefits and how to best utilize them to your advantage.'
              },
              {
                type: BlockType.VIDEO,
                data: 'https://bcove.video/4iFbmWP',
              }
            ]
          },
          {
            categoriesTitle: 'Premium Neighborhood Reports',
            categoriesUrl: 'premium-neighborhood-report',
            data: [
              {
                type: BlockType.TEXT,
                data: 'Unlock the essence of every neighborhood with Realist Premium Neighborhood Reports, a suite of exclusive reports powered by NeighborhoodScout.'
              },
              {
                type: BlockType.TEXT,
                data: 'Check out the tutorial video below to uncover every aspect of your community, from real estate data to economics & demographics information, and schools data to trends & forecasts. With our comprehensive reports, you\'ll gain unparalleled market intelligence to make informed decisions tailored to your needs.'
              },
              {
                type: BlockType.VIDEO,
                data: 'https://resources.Cotality.com/realistpremiumneighborhoodreports-training'
              }
            ]
          },
          {
            categoriesTitle: 'RealMailers Postcards',
            categoriesUrl: 'realmailers-postcards',
            data: [
              {
                type: BlockType.TEXT,
                data: 'Welcome to the <b>RealMailers Postcards</b> tutorial! Learn how to seamlessly create, customize, and send postcards directly from Realist to reach potential clients.'
              },
              {
                type: BlockType.TEXT,
                data: 'With RealMailers, you can:',
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Easily export property records, including owner names and addresses',
                  'Customize your marketing postcards with professional templates',
                  'Target specific neighborhoods or areas using advanced map tools',
                  'Reach up to 3,000 properties per export with a few simple clicks',
                  'Enjoy a <b>6-month free trial</b>, then continue for just <b>$4.99 per month</b>'
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'Watch the tutorial below to see how easy it is to integrate RealMailers with Realist and take your marketing strategy to the next level!',
              },
              {
                type: BlockType.VIDEO,
                data: 'https://bcove.video/3U7ibFY',
              }
            ]
          },
          {
            categoriesTitle: 'Building Permits Report',
            categoriesUrl: 'building-permits-report',
            data: [
              {
                type: BlockType.TEXT,
                data: 'Welcome to the <b>Building Permits Report</b> tutorial! Discover how to access detailed building permit data directly within Realist to enhance your property insights.'
              },
              {
                type: BlockType.TEXT,
                data: 'With the Building Permits Report, you can:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  'Access <b>175 million+</b> building permits from across the country',
                  'Get comprehensive details on construction history, renovations, and upgrades',
                  'Stay informed about local contractors, project types, and dates',
                  'Improve your property evaluations with more robust and accurate information'
                ]
              },
              {
                type: BlockType.TEXT,
                data: 'Watch the tutorial below to see how easy it is to integrate and use Building Permits Reports in Realist for smarter decision-making!',
              },
              {
                type: BlockType.VIDEO,
                data: 'https://bcove.video/3NImV1j',
              },
            ]
          },
          {
            categoriesTitle: 'Document Image',
            categoriesUrl: 'document-image',
            data: [
              {
                type: BlockType.TEXT,
                data: 'Welcome to the Document Image tutorial! Checkout the video below on how  to order Document Images using the "Add to Cart/Checkout" process in Realist:'
              },
              {
                type: BlockType.LIST_DOTS,
                data: [
                  '<b>Select Document Image:</b> Navigate to the desired property to access the Property Details Report and click the hyperlinked Document Number(s) that show up in the Last Sales and Transaction History section. Review the available document and click to add the desired ones to your cart.',
                  '<b>Review Your Cart:</b> Click on the shopping cart icon to review your selected images. Adjust or remove items as needed before proceeding to checkout.',
                  '<b>Checkout and Confirm:</b> Complete the checkout process by entering payment details. Confirm your order and proceed to access the documents'
                ]
              },
              {
                type: BlockType.VIDEO,
                data: 'https://bcove.video/3VtzvWh',
              },
            ]
          }
        ]
      },
    ]
  },
  {
    title: 'FAQ',
    url: RootUserGuideRoutes.FAQ,
    pageContent: []
  },
];
